import { api } from "@/lib/api";
import type { MyActivityRelationship } from "@/lib/my-activity";
import { Permission, useCan } from "@/components/session-provider";

/**
 * The ticket API, as the front end sees it.
 *
 * One module so every screen agrees on the shape, and so a field the server stopped
 * sending is a TypeScript error in one place rather than `undefined` rendered as blank in
 * four. `strict` is on and CLAUDE.md bans `any` in code touching ticket data, so these
 * types are the contract.
 */

/**
 * ⚠️ EXACTLY the members of the server's `TicketStatus`, and there is a test that says so.
 *
 * "Triage" used to be here and has never existed in the API. It reached the queue's status
 * filter as an option, and choosing it sent a value the endpoint could not bind — so the
 * whole request failed and the screen reported that nothing matched the filter, which was a
 * statement about the caller's own data and untrue.
 */
export type TicketStatus = "New" | "InProgress" | "AwaitingUser" | "OnHold" | "Resolved" | "Closed";

export type TicketPriority = "Low" | "Normal" | "High" | "Urgent" | "Critical";

export interface TicketSummary {
  number: string;

  /** Short sequential number for reading aloud. Never a lookup key — links use `number`. */
  displayNumber: number;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  teamName: string;
  categoryName: string;
  siteName: string | null;
  requesterName: string;

  /**
   * Who they are, so their face can be drawn beside the name (D-38).
   *
   * ⚠️ `hasAvatar` is what stops a page of 25 rows firing 25 image requests that 404 for
   * everybody who has never uploaded a picture. Still no email and no UPN in a list.
   */
  requesterUserId: string;
  requesterHasAvatar: boolean;

  /**
   * The requester's job title matches a VIP rule, or an administrator said so.
   *
   * ⚠️ Presentation only. VIP grants nothing and hides nothing — the server never consults
   * it in an authorization decision, and no row is shown or withheld because of it. Marking
   * a row is the entire purpose.
   */
  isRequesterVip: boolean;

  assigneeName: string | null;

  /** Null together with `assigneeName` — an unassigned row has nobody to draw. */
  assigneeUserId: string | null;
  assigneeHasAvatar: boolean;

  createdAtUtc: string;
  updatedAtUtc: string;
  isOpen: boolean;

  /**
   * Resolution deadline and whether it has been missed. Null when no policy applied.
   *
   * Breach is decided by the server rather than by comparing the due date to the browser's
   * clock — a workstation set wrong would otherwise show a different queue from the one
   * beside it.
   */
  resolutionDueAtUtc: string | null;
  isResolutionBreached: boolean;

  /** One word the queue colours by. Decided by the server, never from the browser clock. */
  slaState: SlaState;

  /**
   * The stars the requester gave, or null when nobody has rated this request.
   *
   * ⚠️ Null means UNRATED rather than "not computed": every endpoint returning this
   * shape resolves the ratings for the rows it sends. Nothing here needs to tell the two
   * apart, and nothing should start to — a screen that guesses would render a rated request
   * as unrated and look entirely correct.
   *
   * ⚠️ The stars alone. The comment and the improvement reasons stay on the request's
   * own endpoint: the comment is PHI-capable free text (D-63), and a list is where somebody
   * would read many of them without opening anything.
   */
  ratingStars: number | null;

  /**
   * How many people have asked for this request sooner (D-49). Zero when nobody has.
   *
   * ⚠️ A count of PEOPLE: the cooldown is per person per request, so two is two colleagues
   * blocked rather than one person pressing twice. That is the whole reason it is a number
   * rather than a flag.
   *
   * ⚠️ NOT A PRIORITY OR A SEVERITY. Escalating changes nothing on the request — not the
   * priority, not the status, not the assignee, not the SLA — so the marker must not read as
   * "this is urgent now", or the row would contradict its own priority column. Hence a raised
   * hand rather than a flame, and a neutral tint rather than the destructive red this queue
   * already uses for an SLA breach.
   */
  escalationCount: number;

  /**
   * The person who asked spoke last and nobody has answered them yet.
   *
   * ⚠️ THE SAME PREDICATE THE HOME TILE COUNTS AND THE ATTENTION FILTER SELECTS WITH —
   * `TicketAttention.AwaitingDeskReply`, composed once server-side. Nothing here recomputes
   * it, and nothing should start to: a marker drawn from a second rule would appear on rows
   * the filter does not return, and a reader has no way to tell which of the two is wrong.
   *
   * ⚠️ NOT THE STATUS. A requester's reply already returns an `AwaitingUser` request to
   * `InProgress`, where it looks exactly like work somebody is doing — which is why the row
   * needs this as well as the status pill beside it.
   *
   * ⚠️ False on a resolved or closed request: nobody is owed a reply on finished work. And
   * it moves on PUBLIC entries only, so an internal note can neither set it nor clear it.
   */
  awaitingDeskReply: boolean;
}

export type SlaState = "None" | "OnTrack" | "AtRisk" | "Breached" | "Paused" | "Met";

/**
 * How each state reads in the queue.
 *
 * ⚠️ Colour is never the only signal — every row carries the label as text too. A
 * colour-blind reader and a high-contrast theme both lose the hue, and "which of these is
 * about to breach" is not a question to answer with a shade of amber alone.
 *
 * OnTrack and Met are deliberately unstyled. If everything is marked, nothing is: the
 * point of the colour is that the four rows needing attention stand out from the ninety
 * that do not.
 */
export const slaTone: Record<SlaState, { label: string; dot: string; text: string } | null> = {
  None: null,
  OnTrack: null,
  Met: null,
  AtRisk: { label: "Due soon", dot: "bg-warning", text: "text-warning" },
  Breached: { label: "Overdue", dot: "bg-destructive", text: "text-destructive" },
  Paused: { label: "Waiting", dot: "bg-muted-foreground/60", text: "text-muted-foreground" },
};

export interface TicketPage {
  items: TicketSummary[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface TicketDetail {
  number: string;
  displayNumber: number;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  teamName: string;
  categoryName: string;

  /** The same category as an id, so the correction dialog opens on the one it says (D-123). */
  categoryId: string;

  sensitivity: string;

  /**
   * The site this request was raised about — the only site on the response (D-82).
   *
   * A second field carried where the requester's own account says they work. D-43 derives
   * this one from that placement whenever they have one, so the two agreed on every request
   * raised since, and the server no longer sends it.
   */
  siteName: string | null;

  /**
   * The same site as an id, so the correction dialog opens on the one already recorded.
   *
   * ⚠️ Reference data, not a disclosure: `/api/reference` lists every open site to anybody
   * signed in. Null only on a request raised before a site was required (D-100).
   */
  siteId: string | null;

  requesterName: string;

  /**
   * The requester's user id, for anyone who can read the ticket (D-38, amending D-32).
   *
   * ⚠️ This used to be null unless you held `user.manage`, and the two jobs it was doing
   * have been separated. It is an identifier, needed to draw a face. Whether the name is a
   * LINK to that person's record is `canViewPerson` below — still `user.manage`, still
   * re-checked by the People endpoint.
   */
  requesterUserId: string;
  requesterHasAvatar: boolean;

  /**
   * Whether the requester is a VIP (D-34).
   *
   * ⚠️ The same flag the queue row carries. It stopping at the queue was the defect: somebody
   * opened the one row that was marked and the request said nothing about it.
   *
   * ⚠️ Presentation only, in both directions. VIP grants nothing and hides nothing — it is
   * never an authorization input and there is no permission named after it.
   */
  isRequesterVip: boolean;

  /**
   * The number to ring about this request, in E.164, and the extension to ask for.
   *
   * ⚠️ From the TICKET, not from the requester's profile as it stands today. A request is
   * raised from wherever somebody happened to be and answered days later — following the
   * profile would rewrite the callback number on an old ticket the moment they edited it.
   *
   * Null on any request raised before this was collected. The panel omits the row rather
   * than drawing an empty one.
   */
  contactPhone: string | null;
  contactPhoneExtension: string | null;

  /**
   * Whether to render the requester's name as a link to their record.
   *
   * ⚠️ Rendering only, like every other `can*` here. `/people/$id` and the endpoint behind
   * it both require `user.manage`, so flipping this in devtools produces a link that 404s.
   */
  canViewPerson: boolean;

  /**
   * Who actually filed it, when that is not the requester.
   *
   * ⚠️ Null on an ordinary ticket, and it has to be SHOWN when it is not. A request
   * raised on somebody's behalf otherwise reads as though they wrote it — a record
   * saying something untrue about a named person.
   */
  submittedByName: string | null;

  assigneeName: string | null;
  assigneeUserId: string | null;
  assigneeHasAvatar: boolean;

  createdAtUtc: string;
  updatedAtUtc: string;
  resolvedAtUtc: string | null;
  closedAtUtc: string | null;
  onHoldUntilUtc: string | null;
  onHoldReason: string | null;
  isOpen: boolean;
  allowedTransitions: TicketStatus[];

  /**
   * What this caller may do, decided by the server.
   *
   * ⚠️ For RENDERING only. Every action re-checks on its own endpoint against the
   * specific record, so editing these in devtools yields buttons that return 404.
   */
  canComment: boolean;
  canCommentInternal: boolean;
  canAssign: boolean;

  /**
   * There is at least one entry in {@link allowedTransitions}.
   *
   * ⚠️ Derived from that list server-side, not from a permission of its own. It used to be
   * `ticket.update`, which no requester holds — so the moves a requester is entitled to
   * (reopen, D-23; close, D-61) were filtered into the list correctly and then hidden, because
   * this flag turns the whole panel off.
   */
  canChangeStatus: boolean;

  canChangePriority: boolean;

  /**
   * The caller may correct who this request is for — `ticket.requester.change`.
   *
   * ⚠️ Its own flag and its own permission, deliberately not `ticket.update`. Correcting a
   * requester hands a named colleague sight of a PHI-bearing request and takes it away from
   * somebody else; editing a field on one does not. The permission is seeded onto Owner and
   * Super Admin only, so this is false for an ordinary agent until an Owner grants it.
   *
   * Rendering only, as ever — the endpoint re-checks against the record, which is where the
   * permission's Team and Category scopes actually apply.
   */
  canChangeRequester: boolean;

  /**
   * The caller may correct which site this request came from (D-100) — `ticket.update`.
   *
   * ⚠️ A correction, not a disclosure: site is descriptive metadata and never an access
   * boundary (D-14), so this moves nobody's sight of the request. It changes which queue it is
   * found in and which figures it lands in, which is why it is audited and why a requester
   * does not hold it.
   *
   * Rendering only — the endpoint re-checks against the record and answers 404 when the
   * caller cannot reach it.
   */
  canChangeSite: boolean;

  /**
   * The caller may correct which category this request is filed under (D-123).
   *
   * ⚠️ Rendering only, and deliberately the weaker of the two tests the server applies:
   * re-filing ACROSS classifications additionally needs `ticket.route`, checked server-side.
   * So the control can be drawn for somebody whose particular choice is then refused, with a
   * message saying why - which is the right way round. Computing the stronger test here would
   * hide the control from somebody who can legitimately re-file within a classification.
   */
  canChangeCategory: boolean;

  /**
   * The caller may hand this request to another team (D-96).
   *
   * ⚠️ Its own flag, computed server-side from `ticket.route`. Rendering only — the endpoint
   * re-checks against this record and answers 404 when the caller may not reach it.
   */
  canRoute: boolean;

  /**
   * The caller may delete this request — `ticket.delete`.
   *
   * ⚠️ False for almost everybody, by design. The permission is seeded onto Owner and Super
   * Admin and onto no desk role, and it is reserved for system roles (D-151) — the Roles
   * screen cannot write it into any other role. It is the only act that takes a request out
   * of every reading of it, its requester's included.
   *
   * ⚠️ Rendering only. The endpoint re-checks against this record and answers 404 — never 403
   * — when the caller may not reach it.
   */
  canDelete: boolean;

  /**
   * The caller holds `ticket.merge` (D-161). The flat test, like `canRoute`: the endpoint
   * re-checks against this record AND the one it is merged into, and answers 404 for either.
   * Drawn only where the request is open and not itself merged.
   */
  canMerge: boolean;

  /**
   * The caller may run a work timer on this request — `ticket.update`.
   *
   * ⚠️ Its own flag, and no longer the same answer as {@link canChangeStatus}: that one is now
   * derived from the available moves, while this stays on `ticket.update`. Recording somebody's
   * working time is a different act from changing a status.
   */
  canRecordWork: boolean;

  /** The caller is the requester and the ticket is closed, so they may rate it (D-24). */
  canRate: boolean;

  /**
   * The caller may use canned responses — an agent writing aid. A requester replying on
   * their own request is not offered agent phrasing, and the endpoint behind the picker
   * refuses regardless of what this says.
   */
  canUseCannedResponses: boolean;

  /**
   * How this request has been chased, and whether this caller may chase it (D-49).
   *
   * Null when the feature is switched off, so the panel is absent rather than empty.
   */
  escalation: TicketEscalationState | null;

  /**
   * The commitment on this request.
   *
   * All null when no policy existed when it was raised — a ticket with no deadline is not
   * a breached one, and showing "overdue" for something nobody promised would be worse
   * than showing nothing.
   */
  /**
   * ⚠️ The first-reply fields are null for anyone who does not work tickets — the server
   * withholds them, this is not a UI decision (D-38).
   *
   * The resolution target is a promise made to the requester and they see it, met or
   * missed. The first-reply target is an internal measure of how fast the desk picks work
   * up; a red "First reply — overdue" on somebody's own request announces they were let
   * down and gives them nothing to do about it.
   *
   * Do not reintroduce a client-side check here. If these arrive null, they were not sent.
   */
  firstResponseDueAtUtc: string | null;
  resolutionDueAtUtc: string | null;
  firstRespondedAtUtc: string | null;
  isFirstResponseBreached: boolean;
  isResolutionBreached: boolean;

  /** The clock is stopped because the desk is waiting on somebody else. */
  slaPaused: boolean;

  /**
   * Where this request came from, when it did not come from a person (D-120).
   *
   * Null for every request a person raised, which is every request raised before connected
   * apps shipped — so the panel is absent rather than empty, because "raised by nobody" is
   * not a thing to draw.
   */
  source: TicketSource | null;

  /**
   * The merge relationship, either way round (D-161). Null where this request is neither
   * merged nor the target of a merge, so the panel is absent rather than empty.
   *
   * ⚠️ Numbers only, never the other request's subject — the other request is somebody
   * else's. `mergedInto` is null where the reader cannot open the target while `isMerged`
   * still says the request was merged: a requester learns their duplicate was folded into
   * another request and, where that one is not theirs to read, not into whose.
   */
  merge: TicketMerge | null;

  concurrencyToken: string;

  /**
   * When a closed request stops being reopenable (D-182), or null when it is open, resolved,
   * or merged into another request.
   *
   * ⚠️ Sent so the panel can say so BEFORE somebody presses Reopen, rather than finding out
   * from a 409 — `allowedTransitions` already omits `InProgress` once this has passed, so this
   * is for the message, not the gate.
   */
  reopenableUntilUtc: string | null;
}

/** A request named by its two numbers and nothing else (D-4, D-35). */
export interface TicketReference {
  number: string;
  displayNumber: number;
}

export interface TicketMerge {
  isMerged: boolean;
  mergedInto: TicketReference | null;
  mergedAtUtc: string | null;
  mergedFrom: TicketReference[];
}

/**
 * The application that raised a request (D-120).
 *
 * `name` and `key` are null together when the application has since been removed from the
 * registry — which is a true and useful statement, and why the badge is still drawn: losing
 * it would silently relabel the request as one a person raised.
 */
export interface TicketSource {
  name: string | null;
  key: string | null;

  /** The calling system's own reference, so somebody can walk back to the alert. */
  externalReference: string | null;

  /**
   * When the event it reports actually happened.
   *
   * Display only — the deadline is computed from when the request was raised, not from
   * this. Worth showing precisely because the two differ.
   */
  occurredAtUtc: string | null;
}

/**
 * The server's `CommentKind` enum, by name.
 *
 * ⚠️ THIS UNION WAS WRONG FOR MONTHS AND NOTHING FAILED. It read `Reply | InternalNote |
 * SystemEvent` while the server sends `Public | Internal | System`, so the two branches that
 * drew an internal note differently — the amber card and the lock badge — compared against a
 * value that could never arrive, and every internal note rendered exactly like a reply.
 * Nothing leaked (the server excludes notes from anybody who may not read them before
 * serialisation, D2); what was lost was the ONE visual cue telling an agent which of their
 * own words the requester can see. `TicketCommentKindContractTests` compares this union to
 * the enum in both directions, so it cannot drift again.
 */
export type CommentKind = "Public" | "Internal" | "System";

export type CommentAuthorKind = "Requester" | "Agent" | "System";

export interface TicketComment {
  id: string;
  kind: CommentKind;
  authorName: string;

  /**
   * The author, so their face can be drawn beside their reply (D-38).
   *
   * ⚠️ An id beside a name already on screen is not enumeration: internal notes are still
   * excluded server-side before serialisation, so an author whose entry is withheld is not
   * named here either, and no endpoint turns a user id into a person without `user.manage`
   * re-checking.
   */
  authorUserId: string;
  authorHasAvatar: boolean;

  /**
   * Who the entry is from, in role terms, decided server-side.
   *
   * Not derived here by comparing `authorName` to the ticket's requester: two colleagues
   * sharing a display name is ordinary, and misattributing a reply on a request about
   * access to a patient record is not a cosmetic bug.
   */
  authorKind: CommentAuthorKind;
  body: string;
  createdAtUtc: string;
  editedAtUtc: string | null;

  /**
   * The files sent with this entry, in the Files panel's own shape — so the same view route,
   * scan gate and per-file PHI read apply. Empty on every entry written before the thread
   * could carry one. A file on an internal note arrives only with the note (D2).
   */
  attachments: TicketAttachment[];
}

/**
 * How a request has been chased (D-49).
 *
 * ⚠️ Visible to everybody who can read the ticket, including the requester. Escalation is
 * a fact ABOUT the request rather than private correspondence, and hiding it produces the
 * failure it exists to prevent: somebody presses again because nothing said the first press
 * landed, and the desk gets two identical alerts.
 */
export interface TicketEscalationState {
  count: number;
  lastRaisedAtUtc: string | null;
  lastRaisedByName: string | null;

  /** Advisory, like every other `can` here. The endpoint re-checks all of it. */
  canEscalate: boolean;

  /** When this caller's cooldown ends, so the button can say *when*. */
  cooldownEndsAtUtc: string | null;

  raisedByMe: boolean;
}

/**
 * Asks for a request to be looked at sooner.
 *
 * ⚠️ Changes nothing on the ticket — not priority, status, assignee or SLA. It records the
 * ask and notifies whoever can act on it. Returns the refreshed detail so the panel shows
 * the new count without a second round trip.
 */
export function escalateTicket(number: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/escalate`, {
    method: "POST",
  });
}

export function getTicket(number: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}`);
}

/**
 * The thread.
 *
 * Internal notes the caller may not see are excluded server-side, before serialisation —
 * they are not in the response to be filtered. The mock sent everything and hid notes in
 * React, which is defect D2.
 */
export function getComments(number: string): Promise<TicketComment[]> {
  return api<TicketComment[]>(`/api/tickets/${encodeURIComponent(number)}/comments`);
}

/** Files one message may carry. Mirrors the server's cap; the server decides. */
export const MaxAttachmentsPerComment = 10;

/**
 * Posts one thread entry, optionally with files already uploaded to this request.
 *
 * ⚠️ The files are uploaded FIRST, through the ordinary attachment endpoint, and named here by
 * id — so every file has passed the same magic-byte check, size cap, scan gate and per-ticket
 * allowance as one attached from the Files panel. The server checks each id is on this
 * request, uploaded by the caller and not yet sent, and refuses the whole message otherwise.
 */
export function addComment(
  number: string,
  body: string,
  isInternal: boolean,
  attachmentIds: readonly string[] = [],
): Promise<TicketComment> {
  return api<TicketComment>(`/api/tickets/${encodeURIComponent(number)}/comments`, {
    method: "POST",
    body: { body, isInternal, attachmentIds },
  });
}

/**
 * Improves the agent's own not-yet-sent `currentDraft`, grounded in the request's subject,
 * description and its public thread so far — never something unrelated.
 *
 * ⚠️ There is no "write from scratch" mode — `currentDraft` is required. That mode read only
 * the thread, so a brand-new request with nothing said yet had nothing to work from and
 * produced a generic draft asking the requester to repeat what the subject/description
 * already said; removed rather than taught about those two fields, since an agent always
 * types a starting point first anyway.
 *
 * ⚠️ This is a SUGGESTION that lands in the composer for a human to read and edit —
 * never sent on its own, and it cannot decide who sees the message (D-61's Reply /
 * Internal note choice is untouched by it). A 404 here means the feature is off, not
 * configured, or the caller may not comment on this request — the same convention as
 * every other AI endpoint in this product, and the composer works exactly as well
 * without it.
 */
export function draftReply(
  number: string,
  instruction: string | null,
  currentDraft: string,
): Promise<{ replyText: string }> {
  return api<{ replyText: string }>(`/api/tickets/${encodeURIComponent(number)}/reply-draft`, {
    method: "POST",
    body: { instruction, currentDraft },
  });
}

/**
 * What a hold needs beyond the status: when it ends and why.
 *
 * ⚠️ Both are REQUIRED by the server for `OnHold` and refused with a message when absent — the
 * domain will not park a request without an end date (`TicketStatusRules.MaximumHoldDuration`),
 * and the requester is shown the reason. Every other transition sends the status alone.
 */
export interface HoldTerms {
  holdUntilUtc: string;
  holdReason: string;
}

/** The longest a request may be held, in days. Mirrors the server's cap; the server decides. */
export const MaxHoldDays = 14;

export function changeStatus(
  number: string,
  status: TicketStatus,
  hold?: HoldTerms,
): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/status`, {
    method: "POST",
    body: { status, ...(hold ?? {}) },
  });
}

export function changePriority(number: string, priority: TicketPriority): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/priority`, {
    method: "POST",
    body: { priority },
  });
}

/**
 * Points a request at the person it is actually for.
 *
 * ⚠️ A disclosure, not a field edit: the named person gains sight of the request and its
 * history, and the previous requester loses both. The server audits it at High severity,
 * records it on the thread and tells the new requester.
 *
 * ⚠️ An id, never an email — email is display metadata in this system and never a lookup key
 * (D3). The id comes from the requester picker.
 *
 * Who SUBMITTED the request is not touched by this and cannot be: that is an observation the
 * server recorded, and there is no field for it on the way in.
 */
export function changeRequester(number: string, requesterUserId: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/requester`, {
    method: "POST",
    body: { requesterUserId },
  });
}

/**
 * Corrects where a request came from.
 *
 * ⚠️ There is no way to spell "no site". A request must have one (D-100), so the command
 * carries a required id and the server refuses an unknown or closed site rather than storing
 * it — there is no foreign key behind this column, so an id that resolves to nothing would
 * show as a blank site for ever.
 *
 * ⚠️ It does not move the deadline. Site picks the working calendar for a team keeping no
 * hours of its own, and re-running the clock on a correction would change what somebody was
 * promised as a side effect of fixing a label.
 */
export function changeSite(number: string, siteId: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/site`, {
    method: "POST",
    body: { siteId },
  });
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

export interface TicketAttachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  scanStatus: "Pending" | "Clean" | "Infected" | "Failed";
  /**
   * False until a malware scan has passed. The file is still listed — hiding it would
   * leave someone re-uploading something they can see is already there.
   */
  isDownloadable: boolean;
  /**
   * Whether this file may be shown in place, and as what — the SERVER's answer.
   *
   * ⚠️ Never derived here. What may be rendered is decided by `AttachmentPreview` on the
   * server, from the extension and content type it detected from the file's own bytes; a
   * browser deciding "the name ends in .pdf, I will frame it" would be trusting a label the
   * uploader chose. The view endpoint re-checks it and refuses anything this says is `None`.
   *
   * ⚠️ It is `None` for a file that has not passed the malware scan, so a thumbnail is never
   * requested for bytes the gate has not cleared.
   */
  preview: AttachmentPreview;
  uploadedByName: string;
  uploadedAtUtc: string;
}

/**
 * How an attachment may be drawn rather than saved.
 *
 * `Image` goes in an `<img>`; `Document` is a PDF and goes in a frame of its own. `None` is
 * everything else, and is what an unrecognised value must behave as.
 */
export type AttachmentPreview = "None" | "Image" | "Document";

/**
 * The list, plus whether this caller can add to it.
 *
 * `canUpload` comes from the server for the same reason every other permission does:
 * deciding it in the browser is how the mock leaked privilege into the front end (D1).
 * It governs rendering only — the upload endpoint re-checks the feature flag, the
 * permission and the storage configuration itself.
 */
export interface AttachmentList {
  items: TicketAttachment[];
  canUpload: boolean;
  /** Set only when there is something worth telling the user, e.g. the feature is off. */
  unavailableReason: string | null;
}

/**
 * The per-file cap, mirrored so the browser can refuse an obviously oversized file
 * without spending a round trip on it.
 *
 * ⚠️ A mirror, not the rule. The server enforces this — and enforces it on the bytes it
 * actually received, before the form is even parsed. Nothing here is load-bearing.
 */
export const MaxAttachmentBytes = 5 * 1024 * 1024;

/**
 * The per-ticket total, mirrored for the same reason and with the same caveat.
 *
 * Used on the new-request form, where several files are chosen before any of them is
 * sent — so without it the person learns their fourth file put them over the limit only
 * after the request has already been created.
 */
export const MaxTicketAttachmentBytes = 50 * 1024 * 1024;

/**
 * What the file picker offers.
 *
 * A hint, not a control: the server decides from the file's own bytes, so a renamed
 * executable is refused whatever this says and whatever the picker allowed. It is here
 * so the common case does not involve choosing a file and then being told no.
 */
export const AttachmentAccept =
  ".png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.csv,.log,.docx,.xlsx,.pptx,.zip";

export function getAttachments(number: string): Promise<AttachmentList> {
  return api<AttachmentList>(`/api/tickets/${encodeURIComponent(number)}/attachments`);
}

/**
 * Uploads one file.
 *
 * Sent as multipart rather than base64 JSON so the browser streams it. The server
 * detects the type from the file's own bytes and ignores both the extension and the
 * Content-Type this request declares.
 */
export async function uploadAttachment(number: string, file: File): Promise<TicketAttachment> {
  const form = new FormData();
  form.append("file", file);

  return api<TicketAttachment>(`/api/tickets/${encodeURIComponent(number)}/attachments`, {
    method: "POST",
    // FormData sets its own multipart boundary; the api helper leaves the body alone
    // and still attaches the antiforgery header.
    rawBody: form,
  });
}

/**
 * The download URL.
 *
 * A plain link, not a fetch. The server checks the ticket, checks the scan, audits the
 * read, then redirects to a short-lived single-file SAS on a different hostname — so the
 * bytes never come from this origin and cannot execute against this session.
 */
export function attachmentDownloadUrl(number: string, attachmentId: string): string {
  return `/api/tickets/${encodeURIComponent(number)}/attachments/${encodeURIComponent(attachmentId)}/download`;
}

/**
 * The URL that DRAWS a file instead of saving it.
 *
 * ⚠️ A separate route, never a flag on the download, and the difference is the safety
 * property: the server checks its own closed allowlist before a URL exists, so a file that may
 * not be rendered has no way to be served inline. It then redirects to a short-lived
 * single-file SAS carrying `Content-Disposition: inline` — on the blob host, so the bytes
 * still never come from this origin and cannot execute against this session.
 *
 * ⚠️ Used as a plain `src`, never fetched. The redirect and the antiforgery-free GET are the
 * point: this is a browser loading a picture, not the application reading a file.
 *
 * ⚠️ Every call writes a PHI read event, which is why thumbnails load lazily. Seeing a file is
 * reading it.
 */
export function attachmentViewUrl(number: string, attachmentId: string): string {
  return `/api/tickets/${encodeURIComponent(number)}/attachments/${encodeURIComponent(attachmentId)}/view`;
}

/** Whether this file has something a browser can show. Reads the server's answer, never guesses. */
export function isPreviewable(attachment: TicketAttachment): boolean {
  return attachment.isDownloadable && attachment.preview !== "None";
}

// ---------------------------------------------------------------------------
// Profile pictures
// ---------------------------------------------------------------------------

/** 2 MB. Mirrored for a fast refusal; the server enforces it on the bytes received. */
export const MaxAvatarBytes = 2 * 1024 * 1024;

/**
 * Raster formats only — and note what is absent. SVG is XML that executes script when
 * rendered, and an avatar is the one uploaded file this app renders. The server refuses
 * it from the file's own bytes; this only keeps the picker from offering it.
 */
export const AvatarAccept = ".png,.jpg,.jpeg,.gif,.webp";

export interface AvatarState {
  /** Stored AND cleared by a scan — i.e. actually visible. */
  hasPicture: boolean;

  /**
   * A picture exists and is still being checked.
   *
   * ⚠️ Distinct from `!hasPicture && scanStatus === "Pending"`, which is also what somebody
   * with no picture at all looks like — Pending is the enum's zero, so an empty row reports
   * it. Treating the two as the same polls for ever on behalf of someone not waiting.
   */
  isAwaitingScan: boolean;

  scanStatus: "Pending" | "Clean" | "Infected" | "Failed";

  /** Set when there is something worth saying, e.g. that a scan is still running. */
  message: string | null;
}

/**
 * The state of your OWN picture.
 *
 * The image endpoint cannot answer this: it 404s for "none", "not scanned" and "failed"
 * identically, so that rendering somebody else's face leaks nothing. That is useless to
 * the person waiting to see their own, which is what this is for.
 */
export function getMyAvatarState(): Promise<AvatarState> {
  return api<AvatarState>("/api/me/avatar");
}

export async function uploadAvatar(file: File): Promise<AvatarState> {
  const form = new FormData();
  form.append("file", file);

  return api<AvatarState>("/api/me/avatar", { method: "POST", rawBody: form });
}

export function removeMyAvatar(): Promise<void> {
  return api<void>("/api/me/avatar", { method: "DELETE" });
}

/** Administrator moderation. Removal only — nobody sets a picture on another account. */
export function removeUserAvatar(userId: string): Promise<void> {
  return api<void>(`/api/users/${encodeURIComponent(userId)}/avatar`, { method: "DELETE" });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

/** Unread updates sitting on one request. */
export interface UnreadTicket {
  /** Opaque. What the link is built on. */
  number: string;
  /** Readable. What the badge is drawn beside (D-35). */
  displayNumber: number;
  count: number;
}

export interface Dashboard {
  openCount: number;
  unassignedCount: number;
  assignedToMeCount: number;
  urgentCount: number;
  resolvedThisWeekCount: number;
  awaitingYouCount: number;

  /**
   * Unread in-app notifications — the same rows, and therefore the same number, the bell
   * counts. Zero and empty when in-app notifications are switched off.
   */
  unreadCount: number;
  unreadTicketCount: number;
  unread: UnreadTicket[];

  /** Null when rating requests are switched off. */
  awaitingYourRatingCount: number | null;

  /**
   * The three service-desk counts.
   *
   * ⚠️ Null for anybody who does not work requests, and null because THE SERVER DID NOT SEND
   * THEM — not because this file chose to ignore them. A figure hidden in React is a figure
   * still on the wire, which is the mock's D2 defect in miniature. Nothing here should ever
   * default one of these to 0: the absence is the answer.
   */
  awaitingDeskReplyCount: number | null;
  awaitingYourReplyCount: number | null;
  goneQuietCount: number | null;

  /**
   * Raised by an application that could not say who it was for, and still filed under that
   * application's own account (D-120).
   *
   * Null for anybody who is not the desk, for the reason above: the server did not send it.
   * Never default it to 0 — the absence is the answer.
   */
  awaitingRequesterIdentificationCount: number | null;

  recent: TicketSummary[];

  /**
   * Who has used the desk recently. Null for anybody who is neither desk nor administrator.
   *
   * ⚠️ Null because the SERVER did not send it, never because the browser hid it (D-82). A
   * requester's response does not carry this at all, so there is nothing to read out of the
   * network tab — which is what separates it from mock defect D2.
   */
  presence: Presence | null;
}

/** One person who has used the desk recently. */
export interface PresencePerson {
  displayName: string;

  /** The teams they belong to, joined. Empty for somebody on none — a Super Admin, typically. */
  teamNames: string | null;
}

/**
 * Who is around (D-118).
 *
 * ⚠️ IT MEASURES ACTIVITY, NOT PRESENCE, and `activeWithinMinutes` travels so the screen can
 * say which. A locked laptop with a tab open falls out on its own, because the underlying
 * timestamp is stamped by requests rather than by a heartbeat — so the honest label is "active
 * in the last N minutes" and never "online".
 *
 * ⚠️ NO PER-PERSON TIMESTAMP, deliberately and permanently. "Alex is around" is what a
 * colleague needs; "Alex, last seen 14:02" is a log of when a named employee was at their
 * desk, which D-69, D-70 and D-108 each refused. The field does not exist server-side and a
 * test asserts its absence.
 *
 * ⚠️ The counts are exact; the lists are capped. Never render a count off a list's length —
 * that would report the desk smaller than it is.
 */
export interface Presence {
  agentCount: number;
  otherCount: number;
  activeWithinMinutes: number;
  agents: PresencePerson[];
  others: PresencePerson[];
}

/**
 * Counts, scoped by the same filter and RLS that scope the rows.
 *
 * A count is a disclosure — "there are 47 tickets you cannot see" answers a question the
 * caller was not entitled to ask — so these are never computed outside the caller's view.
 */
/**
 * @param teamId The home screen's own team scope (extends D-116) — narrows every
 *   ticket-derived figure to one desk. Omit for every team the caller can read.
 */
export function getDashboard(teamId?: string): Promise<Dashboard> {
  const query = teamId ? `?${new URLSearchParams({ teamId }).toString()}` : "";
  return api<Dashboard>(`/api/dashboard${query}`);
}

/**
 * How many unread updates the dashboard reported for one request.
 *
 * ⚠️ Keyed on the OPAQUE number (D-4). The readable one is what the badge sits beside and is
 * never what anything looks up by.
 *
 * Returns 0 for a request the server did not name — the list is capped, and in-app
 * notifications may be switched off entirely. Absence is "nothing to show", never "unknown".
 */
export function unreadFor(data: Dashboard | null, number: string): number {
  return data?.unread.find((u) => u.number === number)?.count ?? 0;
}

// ---------------------------------------------------------------------------
// Reference data
// ---------------------------------------------------------------------------

export interface ReferenceCategory {
  id: string;
  name: string;
  sensitivity: string;
  /** Whether the new-request wizard should offer TARS for this category. Rendering only. */
  excludeFromAiAssistant: boolean;
}

export interface ReferenceTeam {
  id: string;
  name: string;
  description: string;
  categories: ReferenceCategory[];
  /** Whether the new-request wizard should offer TARS at all on this team. Rendering only. */
  excludeFromAiAssistant: boolean;
}

export interface ReferenceSite {
  id: string;
  code: string;
  name: string;
  city: string;
}

/**
 * An application permitted to raise requests, for the queue filter that narrows to one
 * (D-120).
 *
 * ⚠️ A name, a handle and an id, and nothing else — not its categories, its defaults, its
 * cap, whether it is enabled, or anything about its credentials. That is the Connected apps
 * screen, behind `integration.manage`.
 *
 * ⚠️ Disabled and since-removed applications are included: the filter is over requests that
 * already exist, and an application switched off last month still raised the ones it raised.
 */
export interface ReferenceSourceApp {
  id: string;
  key: string;
  name: string;
}

export interface Reference {
  teams: ReferenceTeam[];
  sites: ReferenceSite[];
  sourceApps: ReferenceSourceApp[];
  /** What the ticket-creation chat assistant currently calls itself — "Nova" by default. */
  assistantName: string;
  /** What it opens a conversation with, when customised — null means "use the built-in
   * sentence" (see `SupportAgentChat`'s own default). */
  assistantGreeting: string | null;
  /**
   * Whether the sparkle launcher at the top of every screen offers the assistant at all — off
   * leaves it reachable only from inside the new-request wizard, where team/category
   * eligibility already narrows it. See `AssistantLauncher`'s own `eligible` check.
   */
  assistantShowInHeader: boolean;
}

export function getReference(): Promise<Reference> {
  return api<Reference>("/api/reference");
}

/**
 * Creates a ticket.
 *
 * The idempotency key is generated per attempt and reused across retries, so a flaky
 * connection cannot produce two tickets from one submission. The server returns the
 * original rather than creating a duplicate — which matters because the user has no way
 * to tell whether their first attempt landed.
 *
 * Note what is NOT sent: no team, no status, no requester, no priority. The server
 * derives every one of those, and the command type has no field to put them in.
 */
export function createTicket(input: {
  categoryId: string;
  subject: string;
  description: string;

  /**
   * Where the request is coming from.
   *
   * ⚠️ The server REQUIRES a site and refuses a create without one (D-100). A value sent
   * here now WINS over the requester's placement (D-104) — the form pre-selects that
   * placement, so it is still the site on almost every request, but somebody covering a
   * shift elsewhere can say so. Omitting it falls back to the placement.
   */
  siteId?: string;
  idempotencyKey: string;

  /**
   * Where the service desk should ring about this request, and the extension to ask for.
   *
   * ⚠️ Required by the server, which refuses a create without a ringable number and says
   * why. Sent as typed — the server normalises to E.164 and is the authority on the format.
   */
  contactPhone: string;
  contactPhoneExtension?: string;

  /**
   * Who the request is FOR, when that is not the person submitting it.
   *
   * ⚠️ Omit it in the ordinary case. Setting it makes the ticket belong to somebody else
   * — they see it and they are notified about it — and the server refuses unless the
   * caller may create tickets beyond their own. Who actually filed it is recorded and
   * shown on the ticket, so the record never claims they wrote it themselves.
   */
  requesterUserId?: string;

  /**
   * Who should pick it up, when the desk already knows at intake (D-105).
   *
   * ⚠️ Omit it in the ordinary case — a request arrives unassigned and is triaged. The
   * server requires `ticket.create` at Team scope AND `ticket.assign`, and re-checks the
   * named person against the category's team and clearance, so this grants nothing.
   */
  assigneeUserId?: string;
}): Promise<TicketDetail> {
  return api<TicketDetail>("/api/tickets", {
    method: "POST",
    idempotencyKey: input.idempotencyKey,
    body: {
      categoryId: input.categoryId,
      subject: input.subject,
      description: input.description,
      siteId: input.siteId ?? null,
      contactPhone: input.contactPhone,
      contactPhoneExtension: input.contactPhoneExtension ?? null,
      requesterUserId: input.requesterUserId ?? null,
      assigneeUserId: input.assigneeUserId ?? null,
    },
  });
}

/**
 * Who could be given a request filed under this category, before the request exists.
 *
 * ⚠️ Keyed on the category because that is all the form has chosen — it names the team and
 * the classification, which is everything the per-ticket picker reads off a ticket. The
 * create endpoint applies the same rule again, so this list is a convenience and never the
 * control.
 */
export function getAssignableCandidates(categoryId: string): Promise<AssignableUser[]> {
  return api<AssignableUser[]>(
    `/api/tickets/assignable-candidates?categoryId=${encodeURIComponent(categoryId)}`,
  );
}

/**
 * Puts somebody on a ticket that has just been created.
 *
 * ⚠️ The same endpoint the ticket's own participants panel uses, not a create-time
 * shortcut. Every guard runs there — who may manage the list, the site rule, category
 * clearance, and the high-severity audit event — so choosing people on the new-request form
 * is a convenience over the identical grant, never a second path to it.
 */
export function addParticipant(ticketNumber: string, userId: string): Promise<void> {
  return api(`/api/tickets/${encodeURIComponent(ticketNumber)}/participants`, {
    method: "POST",
    body: { userId },
  });
}

export type TicketSortField = "CreatedAt" | "UpdatedAt" | "Status" | "Number" | "Priority";

/**
 * The two "this needs looking at" narrowings the home screen counts and this queue applies.
 *
 * ⚠️ Neither is a status. Both describe a request that has quietly stopped moving while its
 * status still reads as though it were fine, which is why no other filter can express them.
 *
 * ⚠️ Compared member for member against the server's `AttentionFilter` by
 * `QueueFilterContractTests`, in both directions — the seam that produced the phantom
 * `Triage` status (D-60).
 */
export type TicketAttention =
  | "AwaitingDeskReply"
  | "GoneQuiet"
  | "AwaitingRating"
  | "Unread"
  | "AwaitingRequesterIdentification"
  | "Breaching";

export const attentionLabel: Record<TicketAttention, string> = {
  AwaitingDeskReply: "Waiting on a reply from us",
  GoneQuiet: "Gone quiet",
  AwaitingRating: "Waiting for your feedback",
  Unread: "Updates you haven't read",
  AwaitingRequesterIdentification: "Needs a requester",

  // ⚠️ "Past due", never "breaching" — the enum's name is the server's and this is what
  // a person says. It is the STORED deadline the server compares, not D-117's one-hour amber
  // band: that band is drawn in the browser so a wrong workstation clock can pick the wrong
  // shade and never the wrong verdict, and a filter reading it would select a different set
  // from the one every other surface calls late.
  Breaching: "Past due",
};

/**
 * The value that selects requests with NO SITE, in the site filter and in a saved view.
 *
 * ⚠️ A literal rather than an empty string, because an empty query value is what a browser
 * sends for "nothing selected" — the two mean opposite things. It matches
 * `SiteSelection.UnsitedToken` server-side, which is where it is parsed.
 *
 * ⚠️ It selects real work rather than a curiosity: D-50 places somebody the matching rule does
 * not match at no site rather than at a guess, and D-100 leaves every request raised before it
 * with none and no backfill. Without this, those requests are outside every site filter.
 */
export const UNSITED_SITE = "none";

/**
 * The value that selects requests **raised by a person** rather than by an application
 * (D-120).
 *
 * ⚠️ The same spelling as `UNSITED_SITE` and the unrated token, deliberately: three "no
 * value" tokens on one screen spelled three ways is a thing to look up rather than know.
 *
 * ⚠️ Unlike the other two, this bucket is the OVERWHELMING MAJORITY rather than a
 * remainder — every request raised before connected apps shipped has no source. It is
 * selectable anyway, because "show me only what people raised" becomes a real question once
 * applications start filing, and enumerating every application instead goes stale the day
 * somebody registers another.
 */
export const PERSON_RAISED = "none";

/**
 * The value that selects requests NOBODY RATED, in the rating filter and in a saved view.
 *
 * ⚠️ The same spelling as `UNSITED_SITE`, deliberately: two "no value" tokens on one
 * filter bar spelled differently is a URL nobody can read. It matches
 * `RatingSelection.UnratedToken` server-side, which is where it is parsed.
 *
 * ⚠️ "Unrated" is a different question from "rated badly", and it is the one a desk
 * trying to raise its response rate actually has — the survey is asked once, a day after
 * resolution, of the requester alone (D-63), so the unrated set is the work it never reached.
 * Without this token nothing in the product can list it.
 */
export const UNRATED = "none";

/** Every value the rating filter accepts, in the order the filter bar offers them. */
export const RATING_FILTER_VALUES = ["5", "4", "3", "2", "1", UNRATED] as const;

/**
 * How many requests the queue loads at a time, and the choices on offer (D-156).
 *
 * ⚠️ Held in component STATE and nowhere else — not the URL, not a saved view, not storage.
 * "Resets on every session or full refresh" is the ask, and state is the one place that
 * forgets on both. Every value here must be at or under the server's `TicketQuery.MaxPageSize`,
 * or the largest choice is silently clamped to something smaller than the control says; a
 * test on the server side reads this list and holds it to that ceiling.
 *
 * ⚠️ Every row on a page files one PHI read (D-33, D-78). 150 is the ceiling, not a target,
 * and there is no "All" for that reason.
 */
export const QUEUE_PAGE_SIZES = [50, 100, 150] as const;
export type QueuePageSize = (typeof QUEUE_PAGE_SIZES)[number];
export const DEFAULT_QUEUE_PAGE_SIZE: QueuePageSize = 50;

export interface TicketListQuery {
  sortBy?: TicketSortField;
  direction?: "Ascending" | "Descending";
  pageSize?: number;
  cursor?: string;
  openOnly?: boolean;
  /**
   * ASSIGNED to the signed-in user — the agent queue's "My tickets". Resolved server-side
   * from the session, never sent as an id.
   *
   * ⚠️ Not the same question as `raisedByMe`. This one asks what you are working on; that
   * one asks what you have sent to the service desk. "My requests" sent this flag, so it
   * filtered a requester's own list on assignee, matched nothing, and every request anybody
   * raised was invisible to them from the moment they raised it.
   */
  mine?: boolean;

  /**
   * RAISED by the signed-in user — the "My requests" tab (D-47).
   *
   * ⚠️ A flag resolved from the session, like `mine` and `sharedWithMe` and unlike
   * `requesterUserId`: asking about yourself must never require the browser to hold its own
   * user id.
   */
  raisedByMe?: boolean;

  unassigned?: boolean;
  teamId?: string;

  /**
   * The sites to narrow to, where `UNSITED_SITE` selects requests with no site at all.
   *
   * ⚠️ A SET, where this was one id. A request belongs to one site, so "the two clinics I
   * cover" was a question the queue could not ask — and answering it by filtering to one and
   * then the other is not the same thing when what somebody needs is one list.
   *
   * ⚠️ Sent as REPEATED query parameters, and every value is refused rather than dropped if
   * the server cannot read it (D-60). A silently shortened selection is a wider question than
   * the one asked, rendered under a heading naming sites — and every extra row it returns
   * files a PHI read against the caller's name.
   *
   * ⚠️ Ungated, like `teamId` and unlike the two person filters: a site names nobody and is
   * never an access boundary (D-14). Applied on top of the query filter and RLS, so it returns
   * a subset of what the caller could already list.
   */
  siteIds?: string[];

  /**
   * The star ratings to narrow to, where `UNRATED` selects requests nobody rated.
   *
   * ⚠️ Sent as REPEATED query parameters, and every value is refused rather than dropped
   * if the server cannot read it (D-60) — a silently shortened selection is a wider question
   * than the one asked, under a heading naming ratings, and every extra row files a PHI read.
   *
   * ⚠️ Ungated, like `siteIds` and unlike the two person filters: a rating describes the
   * request, not a colleague. What it reads across to IS a person — a queue on one and two
   * stars with the assignee column on is a named colleague's worst-rated work — and that was
   * accepted deliberately: every row was already openable by this reader and the filter only
   * narrows. What must never grow out of it is a score or a rank (D-55).
   */
  ratings?: string[];

  /**
   * The categories to narrow to.
   *
   * ⚠️ Sent as REPEATED query parameters, and an unreadable value is refused rather than
   * dropped (D-60): a silently shortened selection is a WIDER question than the one asked,
   * under a heading naming the categories, and every extra row files a PHI read.
   *
   * ⚠️ No "none" token, unlike `siteIds` and `ratings`. There is no request without a
   * category — it is required at creation and every path that moves one between teams sets a
   * new one (D-96) — so there is no bucket to spell.
   *
   * ⚠️ Ungated: a category describes the request the way a status does and names nobody.
   * Category SENSITIVITY is an access boundary and is enforced in both layers below this
   * filter, so narrowing to a category the reader has no clearance for returns nothing.
   */
  categoryIds?: string[];

  /**
   * Which application raised the request, where the literal `none` is the requests people
   * raised (D-120).
   *
   * ⚠️ The `none` bucket is the OVERWHELMING MAJORITY here, unlike the equivalent bucket on
   * sites: every request raised before connected apps shipped has no source, and most raised
   * afterwards will not either. It is selectable regardless, because "show me only what
   * people raised" becomes a real question once applications start filing — and leaving it
   * implicit would mean the only way to ask it is to enumerate every application, which goes
   * stale the day somebody registers another.
   *
   * ⚠️ Ungated: an application is not a person. It narrows on top of the query filter and
   * RLS like every other filter here.
   */
  sourceIds?: string[];

  /**
   * Requests raised BY a particular person — the people screen's question.
   *
   * ⚠️ Unlike `mine`, this is a caller-supplied id, and the server treats it accordingly:
   * ignored unless the caller holds `user.manage` or it is their own. It can only narrow
   * within the query scope and RLS, so it never surfaces a ticket that was not already
   * readable.
   */
  requesterUserId?: string;

  /**
   * Requests assigned to a particular agent.
   *
   * ⚠️ Like `requesterUserId` and unlike `mine`, this is a caller-supplied id, and the
   * server treats it accordingly: ignored unless the caller holds `ticket.assign` — the
   * same permission that already returns the roster this picker is built from (D-31). It
   * narrows within the query scope and RLS, so filtering by an agent on a team you cannot
   * see returns an empty queue rather than a wider one.
   */
  assigneeUserId?: string;

  /**
   * Only requests raised by a VIP.
   *
   * ⚠️ Ungated, unlike `requesterUserId` and `assigneeUserId`, because it names nobody —
   * it describes the tickets the way `teamId` and `status` do. Applied on top of the query
   * filter and RLS like every other filter here, so it returns a subset of what the caller
   * could already list.
   */
  vipOnly?: boolean;

  /**
   * Requests somebody invited the signed-in user onto — the "Shared with me" tab (D-47).
   *
   * ⚠️ A flag, like `mine` and unlike `requesterUserId`, and for a stronger reason than
   * convenience: the server resolves it from the session, so there is no shape of this
   * request that asks which tickets a NAMED colleague was invited onto. That is a question
   * about somebody else's involvement, and no screen asks it.
   */
  sharedWithMe?: boolean;

  /**
   * Requests raised by the signed-in user's direct reports — "My team's requests" (D-47).
   *
   * ⚠️ Also a flag resolved from the session. There is no `managerUserId` parameter and
   * there must never be one.
   *
   * ⚠️ Returns only reports who have AGREED to share (D-46), and nothing at all when an
   * administrator has switched `reports.manager-access` off. Both are decided server-side;
   * the browser cannot tell an opted-out report from somebody with no reports, which is
   * the intended answer rather than a gap.
   */
  myReports?: boolean;

  /**
   * Where a figure on `/my-activity` lands (D-169).
   *
   * ⚠️ **It exists because the three flags above INTERSECT.** `raisedByMe` and `sharedWithMe`
   * narrow different columns, so sending both selects requests somebody raised AND was
   * separately invited onto — very nearly nothing. Sending neither is worse rather than
   * better: an unfiltered readable set also holds a consenting direct report's requests
   * (D-46), so a figure counted over "mine and the ones shared with me" and drilled through
   * that way opens a WIDER list than the number it came from. That is D-110's defect.
   *
   * ⚠️ The server reads it through the SAME pin `/api/my-activity` applies, never a second
   * copy — which is what stops a figure disagreeing with the list it opens (D-70).
   *
   * ⚠️ **Refused server-side beside `raisedByMe`, `sharedWithMe` or `myReports`**, rather than
   * composed with them: those add a second `WHERE` over the same columns and the reader would
   * get an empty list for a question neither control asked (D-60).
   */
  relationship?: MyActivityRelationship;

  status?: TicketStatus;

  /**
   * The statuses to narrow to, as a set — what `status` cannot express.
   *
   * ⚠️ THE DEFECT IT EXISTS FOR. The requester screen's "Resolved" tab sent
   * `status=Resolved`, so a request that had been resolved and then CLOSED appeared under
   * neither "Open" nor "Resolved" — and being closed is what happens to a request whose
   * requester came back and confirmed the fix (D-61), and to every resolved request the desk
   * tidies up afterwards. The most satisfied requesters were the ones whose requests went
   * missing from every tab but "All".
   *
   * ⚠️ Refused server-side rather than dropped if a value is not one the API knows (D-60).
   * A dropped status widens the question under a heading naming statuses — here, every
   * request somebody has ever raised under a heading saying these are the finished ones.
   *
   * ⚠️ NOT a place to invent a member. `TicketStatus` mirrors the server's enum and
   * `QueueFilterContractTests` compares them both directions; a value the API has never had
   * fails to bind before the handler runs, which is D-60's phantom `Triage`.
   */
  statuses?: TicketStatus[];

  minimumPriority?: TicketPriority;

  /**
   * When the request was RAISED — an ISO instant, inclusive.
   *
   * ⚠️ An instant rather than a date. A "date" filter sent as `2026-08-12` would mean a
   * different eight hours to the server than it did to the person who picked it, and every
   * request raised near midnight would land in the wrong day.
   *
   * ⚠️ The conversion happens in the browser, but NOT because this is the only layer that
   * knows the reader's zone — that was the reasoning here, and it was wrong. The server
   * resolves the authoritative answer (`TimeZones.ResolveAsync`) and sends it down; the
   * browser used its own instead, so a reader away from their site was silently given a
   * window around a different midnight. It converts here using the zone from the session —
   * see `startOfDayUtc` in `@/lib/time`, which requires one.
   */
  raisedFromUtc?: string;

  /**
   * The end of the range, EXCLUSIVE — midnight at the start of the day after the one picked.
   *
   * ⚠️ Exclusive on purpose. An inclusive bound has to be spelled as the last instant of a
   * day, and every version of that is wrong by some fraction of a second, which reads as a
   * filter that drops rows at random. See `endOfDayExclusiveUtc`.
   */
  raisedToUtc?: string;

  /**
   * When the request was RESOLVED — an ISO instant, inclusive.
   *
   * ⚠️ A DIFFERENT QUESTION from the pair above, and both are real. The raised range
   * answers "what came in", which is the frame every desk-wide rate uses (D-53). This answers
   * "what got finished", which is the frame a person's own work is measured in (D-55) — an agent
   * who spent a month clearing a two-year backlog resolved a great deal and raised nothing.
   *
   * ⚠️ It exists so a figure on /my-activity's work lens can OPEN THE ROWS IT COUNTED
   * (D-70). Without it the largest number on that lens would be a total with no link, or worse a
   * link to a raised-range list holding a different set.
   *
   * ⚠️ Instants, converted here from days using the zone the SERVER resolved — the same
   * rule and the same helpers as the raised range beside it.
   */
  resolvedFromUtc?: string;

  /** The end of the resolved range, EXCLUSIVE — see `raisedToUtc`. */
  resolvedToUtc?: string;

  /**
   * "Waiting on a reply from us" or "gone quiet" — the two home-screen tiles, as a filter.
   *
   * ⚠️ The value is refused server-side rather than dropped if it is not one the API knows
   * (D-60): a dropped filter widens the queue past what was asked for and looks exactly like
   * a working screen.
   */
  attention?: TicketAttention;

  /**
   * Requests that have a file on them (`true`), or that have none (`false`).
   *
   * ⚠️ THREE STATES, and `undefined` is one of them. Both answers are real triage questions —
   * "which of these came with a screenshot" and "which of these arrived with nothing to look
   * at" — so a two-state control could only ask the first, and the second would be unaskable
   * rather than merely absent.
   *
   * ⚠️ It counts a file that is still being scanned and one the scan refused, because the
   * question is what was ATTACHED. Whether a file can be opened is a fact about the file, it
   * changes on a background sweep, and a filter keyed on it would move rows between the two
   * answers while somebody was reading the queue. See `TicketFilter.HasAttachment`.
   *
   * ⚠️ Ungated: it names nobody and carries no filename. A filename is free text the uploader
   * chose and PHI-capable in the most ordinary way, which is why one never travels in a
   * notification either (D-89) — this is a yes or a no about whether one exists.
   */
  hasAttachment?: boolean;

  /**
   * Free text matched against the request's SUBJECT OR DESCRIPTION — the product's only
   * free-text filter.
   *
   * ⚠️ D-179 AMENDS D-78, which stopped at the subject: a substring search over the
   * NARRATIVE is the tool for "find every request that mentions this person", and D-78
   * refused to build it. D-179 accepts that risk explicitly, on the condition that every
   * protection built for the subject search carries over unchanged to the description — see
   * `TicketFilter.SearchContains`. Neither reading discloses a row the caller could not
   * already open; every match still runs under the query filter and RLS.
   *
   * ⚠️ Under three characters is REFUSED with a message, not dropped (D-60). Every row a
   * search returns files a PHI read against the caller's name, so a silently-ignored term
   * would hand back the whole readable queue under a heading saying these are the matches
   * and write a read event for each one.
   */
  search?: string;

  /**
   * The teams to narrow to, as a set (D-180) — additive alongside `teamId`.
   *
   * ⚠️ A SEPARATE key from `teamId`, deliberately, rather than the "one value or many" shape
   * `siteIds` uses. `teamId` is shared with the header's sticky team-scope switcher (D-116),
   * which was built on there being exactly one team filter with one value; making that
   * control multi-select was its own decision this one does not reopen. The two are kept from
   * silently ANDing into a narrower queue than either control shows by each clearing the
   * other when it writes — see where this and `teamId` are set in the queue route.
   *
   * ⚠️ Ungated, like `siteIds`: a team names nobody, and narrows on top of the query filter
   * and RLS like every other filter here.
   */
  teamIds?: string[];

  /**
   * The home screen's attention buckets, as a set (D-180) — additive alongside `attention`,
   * which a tile link still sends as one value.
   *
   * ⚠️ A UNION, never an intersection: "needs a reply, or is past its target" is one triage
   * question, and each bucket narrows its own scope before the buckets are combined — see
   * `TicketAttention.Apply`. Only the buckets that describe the REQUEST are offered by the
   * queue's own control, for the reason `attention` documents.
   */
  attentions?: TicketAttention[];
}

// `startOfDayUtc` and `endOfDayExclusiveUtc` moved to `@/lib/time`, which resolves a day's
// bounds in a GIVEN zone rather than the browser's. They anchored to browser midnight, so a
// reader away from their site was handed a window of the right length around the wrong
// midnight — three hours of the neighbouring day, with nothing on screen saying so.

/**
 * Filtering happens on the server.
 *
 * Fetching everything and filtering in the browser is how the mock leaked internal notes
 * (defect D2). It also does not work: the list is paged, so a client-side filter only
 * ever searches the page it happens to have.
 */
export function listTickets(query: TicketListQuery = {}): Promise<TicketPage> {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    // ⚠️ A set is REPEATED, never joined. `String(["a","b"])` is "a,b", which the server reads
    // as one unparseable site and refuses — and the one-element case would have looked
    // perfectly correct, so the failure would have arrived only for somebody who picked two.
    //
    // ⚠️ An empty array is OMITTED. On the wire "present but empty" cannot be expressed, and
    // the server reads a blank value as nothing selected; sending one would be asking a
    // question the URL has no way to phrase.
    if (Array.isArray(value)) {
      for (const entry of value) params.append(key, String(entry));
      continue;
    }

    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  }

  const qs = params.toString();
  return api<TicketPage>(`/api/tickets${qs ? `?${qs}` : ""}`);
}

/**
 * What a search by the readable number returns — enough to recognise the ticket and to
 * follow the link, and nothing else.
 */
export interface TicketLookup {
  /** The opaque number. This is what every route and API path takes. */
  number: string;
  displayNumber: number;
  subject: string;
  status: TicketStatus;
}

/**
 * Whatever somebody typed into a search box, understood as one identifier or neither.
 *
 * Accepts `412`, `#412`, ` 412 `, and the opaque `TF-7K2M-9QX4` in any case, because a
 * person pasting an identifier from an email has no idea which of the two they hold.
 *
 * ⚠️ Exact values only. There is no prefix or range form, and there must not be one: the
 * readable number is sequential, so "everything starting with 4" is an enumeration tool
 * rather than a search. The server enforces this independently — this function only
 * decides which of the two lookups to run.
 */
export function parseTicketQuery(
  input: string,
): { kind: "number"; value: string } | { kind: "displayNumber"; value: number } | null {
  const trimmed = input.trim();
  if (trimmed === "") return null;

  // The opaque number, with or without its TF- prefix typed in lower case.
  if (/^TF-[0-9A-HJKMNP-TV-Z]{4}-[0-9A-HJKMNP-TV-Z]{4}$/i.test(trimmed)) {
    return { kind: "number", value: trimmed.toUpperCase() };
  }

  // The readable number, with an optional # people copy along with it.
  const digits = /^#?(\d{1,9})$/.exec(trimmed);
  if (digits) {
    const value = Number(digits[1]);

    // The sequence starts at 1. Number() cannot overflow at nine digits, so a positive
    // result here is always a value the server can look up.
    if (value > 0) return { kind: "displayNumber", value };
  }

  return null;
}

/**
 * Resolves a readable number to the ticket it names (D-33).
 *
 * ⚠️ Returns null for BOTH "no such ticket" and "not yours to see" — the server sends 404
 * for each, deliberately, so the two cannot be told apart. Callers must not report which
 * one happened, because they do not know and neither does anyone else.
 */
export async function lookupTicketByDisplayNumber(
  displayNumber: number,
): Promise<TicketLookup | null> {
  try {
    return await api<TicketLookup>(`/api/tickets/resolve/${displayNumber}`);
  } catch {
    // Fails closed to "nothing found". A 404, a 429 and a network error all mean the same
    // thing to a search box: it cannot offer you a ticket.
    return null;
  }
}

/** Ordered most urgent first, for sorting and for choosing a colour. */
export const priorityRank: Record<TicketPriority, number> = {
  Critical: 5,
  Urgent: 4,
  High: 3,
  Normal: 2,
  Low: 1,
};

/**
 * Status as the SERVICE DESK would say it. The API sends PascalCase enum names.
 *
 * ⚠️ `AwaitingUser` read "Awaiting you" here, to everybody, and that is the defect this
 * pair of maps exists for. To the person who raised the request it was correct. To the
 * agent reading a queue of ninety it said the desk owes an answer — the exact inversion of
 * what the status means, on the one status where being wrong costs the most, because it is
 * the status that pauses the SLA clock. Nobody chases a request that says it is waiting on
 * them and is not.
 *
 * ⚠️ Only this one member differs by audience. Every other status describes the REQUEST
 * rather than a person and reads identically to both, so `statusLabelForRequester` inherits
 * them rather than restating them — a second copy that does not vary is a copy somebody
 * will later make vary by accident.
 *
 * ⚠️ Mirrored by `TicketStatusText` on the server, which the notification templates render.
 * `TicketStatusTextContractTests` compares the two, both directions. A screen and a mailbox
 * calling one status two different things is how "Status is now AwaitingUser" reached real
 * inboxes for months while the queue looked fine.
 */
export const statusLabel: Record<TicketStatus, string> = {
  New: "New",
  InProgress: "In progress",
  AwaitingUser: "Awaiting requester",
  OnHold: "On hold",
  Resolved: "Resolved",
  Closed: "Closed",
};

/**
 * Status as the person who RAISED the request would say it.
 *
 * ⚠️ "Awaiting your reply", not "Awaiting you". The old wording was ambiguous in the way
 * that matters most: it never said what was wanted. This says the request is stopped until
 * they answer, which is the whole content of the status and the reason its clock is paused.
 */
export const statusLabelForRequester: Record<TicketStatus, string> = {
  ...statusLabel,
  AwaitingUser: "Awaiting your reply",
};

/**
 * Which wording this reader gets, for every status on every screen.
 *
 * ⚠️ THE AUDIENCE IS THE SESSION, NOT THE ROW, and that is a deliberate simplification.
 * The precise question is "is this YOUR request", which is what the server asks when it
 * composes a notification — but a queue row carries no requester identity to ask it of
 * (`requesterUserId` is behind `user.manage`, D-38), and inventing one would mean putting a
 * person id on every row, which is what D-42 refused because it turns a queue into a
 * directory. So the reader is classified once instead.
 *
 * ⚠️ `ticket.comment.internal` is the test, because it is what `ServiceDeskStaff.Includes`
 * already uses server-side to decide who is shown the first-reply target (D-38) and whose
 * reply stops that clock (D-61). A fourth definition of who works at the desk is how those
 * come to disagree.
 *
 * ⚠️ ACCEPTED IMPRECISION, stated rather than hidden: an agent looking at a request THEY
 * raised to another team reads "Awaiting requester" about themselves. It is accurate and
 * slightly impersonal, which is the right way round — the alternative wording would tell an
 * agent scanning their queue that the desk owes ninety answers it does not owe.
 *
 * ⚠️ Rendering only. Nothing branches on this but words.
 */
export function useStatusLabel(): (status: TicketStatus) => string {
  const isDesk = useCan(Permission.TicketCommentInternal);
  const map = isDesk ? statusLabel : statusLabelForRequester;

  return (status: TicketStatus) => map[status];
}

/**
 * How a status pill reads.
 *
 * Every entry carries a ring as well as a tint. A flat 15%-opacity fill has almost no
 * edge against a card in light mode and none at all against a dark one, so the pills read
 * as grey smudges — the ring is what makes them look deliberate in both themes without
 * raising the colour to the point where seven of them in a column start shouting.
 *
 * ⚠️ Colour is never the only signal. Every pill carries `statusLabel` as text, because a
 * colour-blind reader and a high-contrast theme both lose the hue.
 */
export const statusTone: Record<TicketStatus, string> = {
  New: "bg-primary/15 text-primary ring-1 ring-inset ring-primary/25",
  InProgress: "bg-accent text-accent-foreground ring-1 ring-inset ring-primary/30",
  AwaitingUser: "bg-warning/15 text-warning ring-1 ring-inset ring-warning/30",
  OnHold: "bg-muted text-muted-foreground ring-1 ring-inset ring-border",
  Resolved: "bg-success/15 text-success ring-1 ring-inset ring-success/30",
  Closed: "bg-muted text-muted-foreground ring-1 ring-inset ring-border",
};

/**
 * How the button that MOVES a ticket to a status reads.
 *
 * ⚠️ Distinct from {@link statusTone}, which describes the status a ticket is in. This
 * describes an action somebody is about to take, and the two want opposite things: a pill
 * has to sit quietly in a column of ninety, and a button has to be obviously pressable.
 *
 * The two forward moves — picking work up and finishing it — are filled, because they are
 * what an agent is here to do. Everything else is tinted: still coloured, still clearly a
 * button, but not competing with the action that matters. All of it is built from theme
 * tokens, so each one keeps its meaning and its contrast when the theme flips; a hard-coded
 * `bg-green-600` would be a different colour in dark mode and the same colour as
 * everything else in high contrast.
 */
/**
 * What the button that MOVES a ticket says on it.
 *
 * ⚠️ A VERB, and it takes the current status as well as the target. `statusLabel` names a
 * state a ticket is in — "Closed", "In progress" — and a button labelled with a noun tells
 * somebody what they will have done rather than what pressing it does. That was survivable
 * while only agents saw this panel, because an agent presses the same five buttons daily. It
 * is not survivable now that a requester sees it (D-61): their two buttons were "Closed" and
 * "In progress", and neither reads as "I'm done with this" or "this isn't fixed".
 *
 * ⚠️ `from` is needed because InProgress is two different actions. Arriving there from New,
 * Awaiting or On hold is the desk picking work up; arriving from Resolved or Closed is a
 * reopen. Same mapping as `TicketStatusRules.IsReopen` on the server — wording only, and
 * nothing here decides whether the move is allowed. The server sends the list.
 */
export function statusActionLabel(from: TicketStatus, to: TicketStatus): string {
  switch (to) {
    case "InProgress":
      return from === "Resolved" || from === "Closed" ? "Reopen" : "Start work";
    case "Resolved":
      return "Resolve";
    case "Closed":
      return "Close";
    case "AwaitingUser":
      return "Awaiting requester";
    case "OnHold":
      return "Put on hold";
    default:
      return statusLabel[to];
  }
}

export const statusActionTone: Record<TicketStatus, string> = {
  InProgress: "border-transparent bg-primary text-primary-foreground hover:opacity-90",
  Resolved: "border-transparent bg-success text-success-foreground hover:opacity-90",
  AwaitingUser: "border-warning/40 bg-warning/10 text-warning hover:bg-warning/20",
  OnHold: "border-border bg-muted text-muted-foreground hover:bg-muted/70",
  Closed: "border-border bg-secondary text-secondary-foreground hover:bg-secondary/70",

  // Reopening is a correction rather than a step forward, so it is outlined rather than
  // filled — findable, and never the button somebody presses by muscle memory.
  New: "border-primary/40 bg-transparent text-primary hover:bg-primary/10",
};

/**
 * Relative age from a UTC timestamp.
 *
 * Computed against the real clock. The mock froze "now" at a fixed instant, so every
 * relative time was wrong the moment it was shown, in a way that looked plausible.
 */
export function ageOf(iso: string): string {
  const hours = (Date.now() - new Date(iso).getTime()) / 3_600_000;

  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

// ---------------------------------------------------------------------------
// Saved views
// ---------------------------------------------------------------------------

/**
 * A queue filter somebody saved for themselves.
 *
 * ⚠️ A saved view cannot widen what its owner sees. It is the same typed filter the
 * queue already accepts, applied on top of the server's query filter and row-level
 * security — there is no stored predicate, so there is nothing for it to widen with.
 *
 * Private to its owner. The API scopes every read and write to the session's user, and an
 * id belonging to someone else returns 404 rather than 403 — so it never confirms that
 * another person's view exists.
 */
export interface SavedView {
  id: string;
  name: string;

  // Tab-like flags: resolved from the session server-side, never sent as ids.
  openOnly: boolean;
  mine: boolean;
  unassigned: boolean;
  vipOnly: boolean;
  sharedWithMe: boolean;
  myReports: boolean;

  // Filter-bar values: seeded into the URL when the view is selected, so every chip on
  // the bar names something that is really being applied.
  teamId: string | null;

  /**
   * The sites the view narrows to, where `UNSITED_SITE` is the no-site bucket.
   *
   * ⚠️ Empty means EVERY SITE, never "no rows". A view that always returned nothing could not
   * be told apart from a broken one, and there is no control that could set it.
   */
  siteIds: string[];

  /**
   * The star ratings the view narrows to, where `UNRATED` is the unrated bucket.
   *
   * ⚠️ Empty means EVERY RATING — rated and unrated alike — never "no rows", for the
   * same reason `siteIds` does.
   *
   * ⚠️ A published view may carry it, unlike the search and the two person filters. It
   * names no colleague, and there is no permission behind it for the list endpoint to silently
   * ignore, which is the less obvious half of why those three cannot be shared.
   */
  ratings: string[];

  /**
   * The categories the view narrows to.
   *
   * ⚠️ Empty means EVERY CATEGORY, never "no rows", for the same reason `siteIds` does.
   * No bucket token: every request has a category (D-96).
   *
   * ⚠️ A published view may carry it. A category names no colleague and there is no
   * permission behind it for the list endpoint to silently ignore — the two halves of why
   * the search and the person filters cannot be shared.
   */
  categoryIds: string[];
  /**
   * Which applications raised the requests this view shows, where `none` is the ones people
   * raised (D-120).
   *
   * ⚠️ A published view may carry it, on the same two grounds as the categories above: an
   * application names no colleague, and there is no permission behind it for the list
   * endpoint to silently ignore.
   */
  sourceIds: string[];

  status: TicketStatus | null;
  minimumPriority: TicketPriority | null;
  attention: TicketAttention | null;

  /**
   * Whether the view asks for requests with a file, without one, or does not ask.
   *
   * ⚠️ THREE STATES, and `null` is one of them. A `boolean` here would make "no opinion" and
   * "nothing attached" the same stored value, so every view anybody has ever saved would come
   * back hiding every request with a screenshot on it.
   */
  hasAttachment: boolean | null;

  /** The raised-date range as `YYYY-MM-DD` days, exactly as the URL holds it. */
  raisedFrom: string | null;
  raisedTo: string | null;

  assigneeUserId: string | null;
  requesterUserId: string | null;
  search: string | null;

  /**
   * Which columns the table showed, in order.
   *
   * ⚠️ Empty means the view expresses NO OPINION and the reader keeps their own layout —
   * which is also how every view saved before this shipped reads. It is not "hide every
   * column", a state the table cannot render.
   */
  columns: string[];

  sortBy: TicketSortField;
  direction: "Ascending" | "Descending";
  sortOrder: number;

  /** Published by an administrator, so it appears in everyone's strip. */
  isShared: boolean;

  /**
   * Why this view cannot be published, or null when it can be.
   *
   * ⚠️ Rendering only, like every permission on `GET /api/me`. The server re-decides it in
   * the entity on every write — a view carrying a subject search or a person filter cannot
   * be published, because a search term is free text somebody typed while reading requests
   * and a person filter is silently ignored for most of the audience, so the view would
   * return a wider queue than its name promises.
   */
  shareRefusal: string | null;
}

/**
 * What creating or updating one sends.
 *
 * ⚠️ Separate from the response rather than `Omit<SavedView, "id">`, because two fields on
 * the response are the server's answers rather than the caller's input: `shareRefusal` is
 * computed, and `isShared` is honoured only for a caller holding `settings.manage`.
 */
export interface SavedViewInput {
  name: string;
  openOnly: boolean;
  mine: boolean;
  unassigned: boolean;
  vipOnly: boolean;
  sharedWithMe: boolean;
  myReports: boolean;
  teamId: string | null;
  siteIds: string[];
  ratings: string[];

  /**
   * The categories the view narrows to.
   *
   * ⚠️ Empty means EVERY CATEGORY, never "no rows", for the same reason `siteIds` does.
   * No bucket token: every request has a category (D-96).
   *
   * ⚠️ A published view may carry it. A category names no colleague and there is no
   * permission behind it for the list endpoint to silently ignore — the two halves of why
   * the search and the person filters cannot be shared.
   */
  categoryIds: string[];
  /**
   * Which applications raised the requests this view shows, where `none` is the ones people
   * raised (D-120).
   *
   * ⚠️ A published view may carry it, on the same two grounds as the categories above: an
   * application names no colleague, and there is no permission behind it for the list
   * endpoint to silently ignore.
   */
  sourceIds: string[];

  status: TicketStatus | null;
  minimumPriority: TicketPriority | null;
  attention: TicketAttention | null;

  /** Three states — see `SavedView.hasAttachment`. */
  hasAttachment: boolean | null;

  raisedFrom: string | null;
  raisedTo: string | null;
  assigneeUserId: string | null;
  requesterUserId: string | null;
  search: string | null;
  columns: string[];
  sortBy: TicketSortField;
  direction: "Ascending" | "Descending";
  sortOrder: number;
  isShared?: boolean;
}

/**
 * The parts of a saved view that behave like a queue TAB rather than like a filter.
 *
 * ⚠️ Deliberately not the whole view. Its team, site, status, priority, dates, people and
 * search are seeded into the filter bar when the view is selected, so the bar always states
 * what is actually being applied and each chip can clear the thing it names. Returning them
 * here as well would produce a filter the bar shows as cleared and the query still applies —
 * a control that appears to do nothing, which is the exact failure the chips exist to prevent.
 *
 * ⚠️ Which is why the three relationship flags DO belong here: `vipOnly`, `sharedWithMe` and
 * `myReports` have no chip on the bar to state them. They were previously lost on save
 * entirely, so a view saved on the VIP tab came back as the plain tab it was saved from.
 */
export function savedViewToBaseQuery(view: SavedView): TicketListQuery {
  return {
    openOnly: view.openOnly || undefined,
    mine: view.mine || undefined,
    unassigned: view.unassigned || undefined,
    vipOnly: view.vipOnly || undefined,
    sharedWithMe: view.sharedWithMe || undefined,
    myReports: view.myReports || undefined,
  };
}

export function getSavedViews(): Promise<SavedView[]> {
  return api<SavedView[]>("/api/saved-views");
}

export function createSavedView(view: SavedViewInput): Promise<SavedView> {
  return api<SavedView>("/api/saved-views", { method: "POST", body: view });
}

export function updateSavedView(id: string, view: SavedViewInput): Promise<void> {
  return api<void>(`/api/saved-views/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: view,
  });
}

export function deleteSavedView(id: string): Promise<void> {
  return api<void>(`/api/saved-views/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Bulk actions
// ---------------------------------------------------------------------------

/**
 * What a bulk action did.
 *
 * ⚠️ Partial success is reported, never hidden. An agent who believes they triaged twenty
 * tickets, three of which were silently refused, has three sitting in a queue nobody is
 * watching — so the screen shows the refusals rather than a success message.
 *
 * A refusal reads the same whether the ticket is out of the caller's scope or does not
 * exist. That is deliberate on the server: distinguishing them would turn a bulk endpoint
 * into a way to discover which ticket numbers are real.
 */
export interface BulkResult {
  requested: number;
  succeeded: number;
  /**
   * ⚠️ `displayNumber` is null exactly when the ticket could not be resolved, which the
   * reason already says in words. It is not a second signal separating "does not exist"
   * from "not visible to you" — those two deliberately share one reason string.
   */
  failed: { number: string; displayNumber: number | null; reason: string }[];
}

export function bulkAssign(numbers: string[], assigneeUserId: string): Promise<BulkResult> {
  return api<BulkResult>("/api/tickets/bulk/assign", {
    method: "POST",
    body: { numbers, assigneeUserId },
  });
}

export function bulkPriority(numbers: string[], priority: TicketPriority): Promise<BulkResult> {
  return api<BulkResult>("/api/tickets/bulk/priority", {
    method: "POST",
    body: { numbers, priority },
  });
}

export function bulkStatus(numbers: string[], status: TicketStatus): Promise<BulkResult> {
  return api<BulkResult>("/api/tickets/bulk/status", {
    method: "POST",
    body: { numbers, status },
  });
}

// ---------------------------------------------------------------------------
// Routing to another team (D-96)
//
// ⚠️ Routing is a DISCLOSURE, not an edit: the destination team's agents gain a PHI-bearing
// request they have never seen, and the routing team usually loses it. Everything below is
// gated on `ticket.route` at the endpoint AND against the specific record, and the server
// refuses a destination that is not cleared for the request's classification — so a screen
// must never present these as ordinary field changes.
// ---------------------------------------------------------------------------

/** One destination category, and whether its team could actually read the request. */
export interface RouteTargetCategory {
  id: string;
  name: string;
  sensitivity: string;

  /**
   * ⚠️ False means the request would be invisible to everybody on the destination team, and the
   * server refuses it. The screen has to say so BEFORE somebody picks it, or the picker is full
   * of options that fail — the `AssignmentCandidates` argument applied to a team.
   */
  teamIsCleared: boolean;
}

export interface RouteTargetTeam {
  id: string;
  name: string;
  description: string;
  isCurrent: boolean;
  categories: RouteTargetCategory[];
}

export interface RouteTargets {
  currentTeamName: string;
  currentCategoryName: string;
  sensitivity: string;
  teams: RouteTargetTeam[];
}

/** The most requests one routing call may move (mirrors `TicketRoutingEndpoints.MaxRouteBatch`). */
export const MAX_ROUTE_BATCH = 10;

export const MAX_ROUTE_NOTE = 1000;

export function getRouteTargets(number: string): Promise<RouteTargets> {
  return api<RouteTargets>(`/api/tickets/${encodeURIComponent(number)}/route-targets`);
}

/**
 * Hands one request to another team.
 *
 * ⚠️ `note` goes on the THREAD and into no notification. An agent explaining why they cannot
 * take a request may reasonably write clinical detail into it, so it stays behind the request's
 * own authorization and never leaves the tenant.
 */
export function routeTicket(
  number: string,
  teamId: string,
  categoryId: string,
  note: string | null,
): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/route`, {
    method: "POST",
    body: { teamId, categoryId, note },
  });
}

/**
 * What the server says after raising a copy of a request with another team (D-125).
 *
 * ⚠️ **Deliberately not a `TicketDetail`.** Copying into a team the caller is not on is the
 * ordinary case — that is the whole feature — so the copy is frequently a request they cannot
 * open a second later. Returning its detail would hand them, through the create path, a record
 * both enforcement layers are about to refuse them (D-120's rule that a write must not become a
 * read).
 */
export interface ClonedTicket {
  /** The opaque number, which is what a link is built from (D-4). */
  number: string;

  /** The readable number, which is what people recognise (D-35). */
  displayNumber: number;

  teamName: string;
  categoryName: string;

  /**
   * Whether this caller can actually open the copy.
   *
   * ⚠️ From the server, which ran the real resource check against the new record — never
   * inferred here from team membership, so it cannot disagree with what happens on the click.
   * False is expected: an IT agent sending work to EMR generally has no EMR queue.
   */
  readableByYou: boolean;
}

/**
 * Raises a copy of one request with another team.
 *
 * ⚠️ `note` goes on BOTH threads and into no notification, for `routeTicket`'s reason: it is the
 * field an agent would write clinical detail into, so it stays behind each request's own
 * authorization and never leaves the tenant.
 *
 * ⚠️ The original is untouched — not its status, assignee, deadline or clock. The copy is a
 * separate request with its own owner and its own commitment, and nobody on the original is
 * notified, because nothing happened to it.
 */
export function cloneTicket(
  number: string,
  teamId: string,
  categoryId: string,
  priority: TicketPriority,
  note: string | null,
): Promise<ClonedTicket> {
  return api<ClonedTicket>(`/api/tickets/${encodeURIComponent(number)}/clone`, {
    method: "POST",
    body: { teamId, categoryId, priority, note },
  });
}

/**
 * What the server says after deleting or restoring a request.
 *
 * ⚠️ **Deliberately not a `TicketDetail`**, and the delete case is why: a moment after the
 * call the request is outside the caller's own query filter, so returning its detail would
 * hand back through this very endpoint a record both enforcement layers have just been told
 * to stop showing. Both numbers travel because they are not interchangeable (D-4, D-35) —
 * `displayNumber` is what the confirmation says, `number` is what the undo addresses.
 */
export interface TicketDeletion {
  number: string;
  displayNumber: number;
}

/**
 * Removes a request from the service desk.
 *
 * ⚠️ **A SOFT DELETE.** The row, its thread, its files, its ratings and its whole ledger
 * history stay exactly where they are — nothing in this product hard-deletes a PHI-bearing
 * record. What goes is every reading of it: the queue, search, every reporting figure, and the
 * list belonging to the person who raised it. That is what "deleted requests do not count
 * towards metrics" means, and it is structural — every figure is computed under the same
 * query filter that now excludes the row.
 *
 * ⚠️ **Reversible** — see {@link restoreTicket}. A delete nobody can undo is indistinguishable
 * from a hard one to everybody who can no longer see the request.
 *
 * ⚠️ Nobody is notified. Every notification is composed from what its recipient may see
 * (D-17), and after this the request is precisely what they may not — so the message would be
 * the disclosure. The record of who did it is a High-severity row in the ledger.
 */
export function deleteTicket(number: string): Promise<TicketDeletion> {
  return api<TicketDeletion>(`/api/tickets/${encodeURIComponent(number)}`, { method: "DELETE" });
}

/**
 * Puts a deleted request back.
 *
 * ⚠️ Addressed by the OPAQUE number (D-4), which is why the delete hands one back: after the
 * delete the request is unreachable through every listing in the product, so the number the
 * caller was just given is the way to it.
 */
export function restoreTicket(number: string): Promise<TicketDeletion> {
  return api<TicketDeletion>(`/api/tickets/${encodeURIComponent(number)}/restore`, {
    method: "POST",
    body: {},
  });
}

/** The same operation over a selection — the server runs the identical code path per request. */
export function bulkRoute(
  numbers: string[],
  teamId: string,
  categoryId: string,
  note: string | null,
): Promise<BulkResult> {
  return api<BulkResult>("/api/tickets/bulk/route", {
    method: "POST",
    body: { numbers, teamId, categoryId, note },
  });
}

/**
 * Copies a selection to one destination — one team, one category, one priority, applied to
 * every request selected (D-125's bulk amendment). The server runs the same code path per
 * request the single-request `cloneTicket` does; a request the destination cannot take is
 * refused and listed by name in the result, exactly as a bulk route refusal is.
 */
export function bulkClone(
  numbers: string[],
  teamId: string,
  categoryId: string,
  priority: TicketPriority,
  note: string | null,
): Promise<BulkResult> {
  return api<BulkResult>("/api/tickets/bulk/clone", {
    method: "POST",
    body: { numbers, teamId, categoryId, priority, note },
  });
}

// ---------------------------------------------------------------------------
// Merging duplicates (D-161)
// ---------------------------------------------------------------------------

/**
 * ⚠️ Ten, like routing, and not fifty. Each merge closes a request and tells its requester
 * the desk decided their problem was somebody else's — the server refuses a larger batch.
 */
export const MAX_MERGE_BATCH = 10;

/**
 * Fold this request into another (D-161). The duplicate is closed and linked; nothing is
 * copied — no replies, no files, no participants — and the merge can be undone.
 *
 * ⚠️ `targetNumber` is the OPAQUE number (D-4): resolve the readable one somebody typed with
 * `lookupTicketByDisplayNumber` first, on Enter, never per keystroke (D-33).
 */
export function mergeTicket(
  number: string,
  targetNumber: string,
  addRequesterAsParticipant: boolean,
  note: string | null,
): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/merge`, {
    method: "POST",
    body: { targetNumber, addRequesterAsParticipant, note },
  });
}

/** Undo a merge: the request goes back to the status it had and the link is cleared. */
export function unmergeTicket(number: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/unmerge`, {
    method: "POST",
    body: {},
  });
}

/** The same operation over a selection, into one target — identical code path per request. */
export function bulkMerge(
  numbers: string[],
  targetNumber: string,
  addRequesterAsParticipant: boolean,
  note: string | null,
): Promise<BulkResult> {
  return api<BulkResult>("/api/tickets/bulk/merge", {
    method: "POST",
    body: { numbers, targetNumber, addRequesterAsParticipant, note },
  });
}

// ---------------------------------------------------------------------------
// Linked teams (D-97)
// ---------------------------------------------------------------------------

export interface TeamLink {
  id: string;
  partnerTeamId: string;
  partnerTeamName: string;
  reason: string;
  createdAtUtc: string;
  createdByName: string;
  revokedAtUtc: string | null;

  /**
   * What this team's agents will actually be able to open on the partner's queue.
   *
   * ⚠️ A link confers REACH and no CLEARANCE, so this can legitimately be empty on a link that
   * is live, correct and audited. Without it the screen would report a working arrangement over
   * an empty queue and nobody would know which of the two was wrong.
   */
  sharedClassifications: string[];

  /** The partner's classifications this team holds no clearance for — named, never counted. */
  unreachableClassifications: string[];
}

export interface TeamLinks {
  teamId: string;
  teamName: string;
  active: TeamLink[];
  ended: TeamLink[];
  candidates: { id: string; name: string; description: string }[];
}

export function getTeamLinks(teamId: string): Promise<TeamLinks> {
  return api<TeamLinks>(`/api/teams/${teamId}/links`);
}

/**
 * Links two teams so each may work the other's requests.
 *
 * ⚠️ The server requires `team.manage` over BOTH teams, and answers 403 with an explanation
 * rather than 404 when the caller administers only one — they picked the partner from a list, so
 * pretending it does not exist would be a lie that reads as a bug.
 */
export function createTeamLink(
  teamId: string,
  partnerTeamId: string,
  reason: string,
): Promise<{ id: string }> {
  return api<{ id: string }>(`/api/teams/${teamId}/links`, {
    method: "POST",
    body: { partnerTeamId, reason },
  });
}

/** Ends a link. One side is enough — ending can only ever narrow access. */
export function endTeamLink(teamId: string, linkId: string): Promise<void> {
  return api<void>(`/api/teams/${teamId}/links/${linkId}`, { method: "DELETE" });
}

export const TEAM_LINK_MAX_REASON = 400;

// ---------------------------------------------------------------------------
// SLA
// ---------------------------------------------------------------------------

export interface SlaPolicy {
  priority: TicketPriority;
  firstResponseMinutes: number;
  resolutionMinutes: number;
  clock: "BusinessHours" | "Continuous";
}

export interface BusinessHoursDay {
  day: string;
  isOpen: boolean;
  opensAtMinutes: number;
  closesAtMinutes: number;
}

export interface SlaSettings {
  policies: SlaPolicy[];
  hours: BusinessHoursDay[];
}

export function getSlaSettings(): Promise<SlaSettings> {
  return api<SlaSettings>("/api/settings/sla");
}

export function saveSlaPolicy(priority: string, body: Omit<SlaPolicy, "priority">): Promise<void> {
  return api(`/api/settings/sla/policies/${encodeURIComponent(priority)}`, {
    method: "PUT",
    body,
  });
}

export function saveBusinessHours(day: string, body: Omit<BusinessHoursDay, "day">): Promise<void> {
  return api(`/api/settings/sla/hours/${encodeURIComponent(day)}`, { method: "PUT", body });
}

// ---------------------------------------------------------------------------
// Team and category response targets
//
// The two tiers above the organisation's default, which stays in Settings. These are gated on
// sla.manage and additionally checked against the team, so a team administrator reaches the
// teams they administer and no others.
// ---------------------------------------------------------------------------

/** Which rung of the ladder a figure was resolved from. */
export type SlaTier = "Default" | "Team" | "Category";

export interface SlaTarget {
  priority: TicketPriority;
  firstResponseMinutes: number;
  resolutionMinutes: number;
  clock: "BusinessHours" | "Continuous";

  /**
   * ⚠️ Sent by the server, never inferred here. A screen that showed an inherited figure
   * without saying so would invite somebody to "correct" a number that is not set where they
   * are looking, and the edit would land on a row they did not mean.
   */
  tier: SlaTier;
}

export interface CategorySla {
  categoryId: string;
  name: string;
  sensitivity: string;
  isActive: boolean;
  targets: SlaTarget[];
}

export interface OperatingDay {
  day: string;
  isOpen: boolean;
  opensAtMinutes: number;
  closesAtMinutes: number;
}

export interface OperatingHours {
  /**
   * False when the team follows the organisation's week. The days are then the organisation's,
   * sent so an administrator can see what they would be changing from rather than starting a
   * schedule from a blank form.
   */
  isOwnSchedule: boolean;

  /**
   * ⚠️ Null while inheriting, and that is not a missing value. A team on the organisation's
   * week has each request measured at ITS OWN SITE, which no single identifier can express —
   * printing one would name a timezone the clock does not use.
   */
  timeZoneId: string | null;

  days: OperatingDay[];
}

export interface HolidayRule {
  id: string;
  name: string;
  kind: "FixedDate" | "NthWeekday";
  month: number;
  day: number | null;
  ordinal: number | null;
  weekday: string | null;
  observedOnNearestWeekday: boolean;

  /**
   * Days added to the resolved date, for a holiday defined relative to another.
   *
   * ⚠️ This is why "the day after Thanksgiving" is not stored as "the fourth Friday in
   * November". They coincide in 2026 and in 2024 the fourth Friday falls six days BEFORE
   * Thanksgiving, because that November has five Fridays.
   */
  offsetDays: number;

  isObserved: boolean;

  /**
   * ⚠️ Computed by the server, never here. "The fourth Thursday in November" is a rule somebody
   * can mistype into something that looks right and lands on the wrong day, and seeing the dates
   * is the only way to catch it — or to notice a rule that resolves to nothing at all, which
   * otherwise reads as a holiday that silently never closes anything.
   */
  nextDates: string[];
}

export interface HolidaySet {
  isOwnSet: boolean;
  holidays: HolidayRule[];
}

export interface TeamSla {
  teamId: string;
  teamName: string;
  hours: OperatingHours;
  holidays: HolidaySet;
  targets: SlaTarget[];
  categories: CategorySla[];
  /** The number to ring for this team's urgent issues out of hours. Null if unset. */
  onCallPhone: string | null;
}

/**
 * Whether a team is open right now, and what it normally looks like — read before any ticket
 * exists, so the new-request wizard can warn somebody the desk is not staffed.
 */
export interface TeamAvailability {
  isOpenNow: boolean;
  timeZoneId: string;
  days: OperatingDay[];
  onCallPhone: string | null;
}

/** Starts a team's own list as a copy of the organisation's. */
export function adoptTeamHolidays(teamId: string): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/holidays/adopt`, { method: "POST" });
}

/**
 * The whole rule, on edit as well as create.
 *
 * ⚠️ It carries no `isObserved`. Whether the desk works a day is a different decision from when
 * that day falls — folding them together would mean fixing a typo in a rule silently switched
 * the holiday back on.
 */
export type HolidayRuleBody = Pick<
  HolidayRule,
  | "name"
  | "kind"
  | "month"
  | "day"
  | "ordinal"
  | "weekday"
  | "observedOnNearestWeekday"
  | "offsetDays"
>;

export function setTeamHolidayObserved(
  teamId: string,
  holidayId: string,
  isObserved: boolean,
): Promise<void> {
  return api(
    `/api/teams/${encodeURIComponent(teamId)}/holidays/${encodeURIComponent(holidayId)}/observed`,
    { method: "PUT", body: { isObserved } },
  );
}

export function updateTeamHoliday(
  teamId: string,
  holidayId: string,
  body: HolidayRuleBody,
): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/holidays/${encodeURIComponent(holidayId)}`, {
    method: "PUT",
    body,
  });
}

export function removeTeamHoliday(teamId: string, holidayId: string): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/holidays/${encodeURIComponent(holidayId)}`, {
    method: "DELETE",
  });
}

/** Reverts the whole list to the organisation's. */
export function clearTeamHolidays(teamId: string): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/holidays`, { method: "DELETE" });
}

export function addTeamHoliday(teamId: string, body: HolidayRuleBody): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/holidays`, { method: "POST", body });
}

// The organisation's list, in Settings. Full CRUD — the desk's holiday schedule changes.
export function getOrganisationHolidays(): Promise<HolidayRule[]> {
  return api<HolidayRule[]>("/api/settings/sla/holidays");
}

export function addOrganisationHoliday(body: HolidayRuleBody): Promise<void> {
  return api("/api/settings/sla/holidays", { method: "POST", body });
}

export function updateOrganisationHoliday(holidayId: string, body: HolidayRuleBody): Promise<void> {
  return api(`/api/settings/sla/holidays/${encodeURIComponent(holidayId)}`, {
    method: "PUT",
    body,
  });
}

export function setOrganisationHolidayObserved(
  holidayId: string,
  isObserved: boolean,
): Promise<void> {
  return api(`/api/settings/sla/holidays/${encodeURIComponent(holidayId)}/observed`, {
    method: "PUT",
    body: { isObserved },
  });
}

/**
 * ⚠️ Reaches further than switching one off: a team taking its own list copies what the
 * organisation has AT THAT MOMENT, so a deleted rule is one no future team list starts from.
 * Teams already holding their own copy keep theirs.
 */
export function deleteOrganisationHoliday(holidayId: string): Promise<void> {
  return api(`/api/settings/sla/holidays/${encodeURIComponent(holidayId)}`, { method: "DELETE" });
}

/** ⚠️ All seven days, in one call — a team's hours are set as a week. */
export function saveTeamHours(
  teamId: string,
  body: { timeZoneId: string; days: OperatingDay[] },
): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/hours`, { method: "PUT", body });
}

/** Returns the team to the organisation's week, measured at each request's own site. */
export function clearTeamHours(teamId: string): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/hours`, { method: "DELETE" });
}

export function getTeamSla(teamId: string): Promise<TeamSla> {
  return api<TeamSla>(`/api/teams/${encodeURIComponent(teamId)}/sla`);
}

export function saveTeamOnCall(teamId: string, phone: string): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/on-call`, {
    method: "PUT",
    body: { phone },
  });
}

export function clearTeamOnCall(teamId: string): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/on-call`, { method: "DELETE" });
}

/**
 * Whether `teamId` is open right now, for a request from `siteId` about `categoryId` — read on
 * the new-request wizard's review step, never gated behind an admin permission (organisational,
 * not PHI, the same trust class as `/api/reference`).
 */
export function getTeamAvailability(
  teamId: string,
  siteId: string,
  categoryId: string,
): Promise<TeamAvailability> {
  const params = new URLSearchParams({ siteId, categoryId });
  return api<TeamAvailability>(
    `/api/teams/${encodeURIComponent(teamId)}/availability?${params.toString()}`,
  );
}

export type SetSlaTargetBody = Omit<SlaTarget, "priority" | "tier">;

export function saveTeamSlaTarget(
  teamId: string,
  priority: string,
  body: SetSlaTargetBody,
): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/sla/${encodeURIComponent(priority)}`, {
    method: "PUT",
    body,
  });
}

export function saveCategorySlaTarget(
  categoryId: string,
  priority: string,
  body: SetSlaTargetBody,
): Promise<void> {
  return api(
    `/api/categories/${encodeURIComponent(categoryId)}/sla/${encodeURIComponent(priority)}`,
    { method: "PUT", body },
  );
}

/** Removes an OVERRIDE, never a commitment — what is left is whatever the next rung says. */
export function clearTeamSlaTarget(teamId: string, priority: string): Promise<void> {
  return api(`/api/teams/${encodeURIComponent(teamId)}/sla/${encodeURIComponent(priority)}`, {
    method: "DELETE",
  });
}

export function clearCategorySlaTarget(categoryId: string, priority: string): Promise<void> {
  return api(
    `/api/categories/${encodeURIComponent(categoryId)}/sla/${encodeURIComponent(priority)}`,
    { method: "DELETE" },
  );
}

/**
 * Minutes as something a person reads.
 *
 * Targets are stored in minutes because "one business day" is only a duration once opening
 * hours are known — but 3600 is not a number anyone can judge, so the screen says
 * "5 business days" and the field underneath still holds the minutes.
 */
export function describeMinutes(minutes: number, businessDayMinutes = 720): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < businessDayMinutes) {
    const hours = minutes / 60;
    return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hr`;
  }

  const days = minutes / businessDayMinutes;
  return `${Number.isInteger(days) ? days : days.toFixed(1)} business ${days === 1 ? "day" : "days"}`;
}

/** "07:00" from 420. The API speaks minutes; an input speaks HH:mm. */
export function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function timeToMinutes(value: string): number {
  const [h, m] = value.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** "8:00 AM" from 480 — a person-facing clock time, distinct from `minutesToTime`'s HH:mm
 * (which feeds an `<input type="time">`). */
function formatClockTime(minutes: number): string {
  const h24 = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const period = h24 < 12 ? "AM" : "PM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return m === 0 ? `${h12} ${period}` : `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

const WEEK_ORDER = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

const DAY_ABBREVIATIONS: Record<string, string> = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

/**
 * "Mon–Fri 8:00 AM–5:00 PM (ET)" — a team's normal hours, collapsed to one short line.
 *
 * ⚠️ Closed days are dropped entirely rather than printed ("Closed Sun"), and consecutive open
 * days sharing identical times fold into one range — the point is a line short enough for a
 * banner, not a full weekly table (that already exists, admin-only, on the team's SLA screen).
 * A day never seen (e.g. the server sent an unrecognised name) is ignored rather than crashing
 * the banner over it.
 */
export function formatBusinessHours(days: OperatingDay[], timeZoneId: string): string {
  const byDay = new Map(days.map((d) => [d.day, d]));
  const ordered = WEEK_ORDER.map((day) => byDay.get(day)).filter((d): d is OperatingDay => !!d);
  const open = ordered.filter((d) => d.isOpen);

  if (open.length === 0) return "Closed every day";

  const groups: { start: OperatingDay; end: OperatingDay }[] = [];

  for (const day of open) {
    const last = groups.at(-1);
    const sameAsLast =
      last &&
      WEEK_ORDER.indexOf(last.end.day as (typeof WEEK_ORDER)[number]) + 1 ===
        WEEK_ORDER.indexOf(day.day as (typeof WEEK_ORDER)[number]) &&
      last.end.opensAtMinutes === day.opensAtMinutes &&
      last.end.closesAtMinutes === day.closesAtMinutes;

    if (sameAsLast && last) {
      last.end = day;
    } else {
      groups.push({ start: day, end: day });
    }
  }

  const parts = groups.map(({ start, end }) => {
    const label =
      start.day === end.day
        ? DAY_ABBREVIATIONS[start.day]
        : `${DAY_ABBREVIATIONS[start.day]}–${DAY_ABBREVIATIONS[end.day]}`;

    return `${label} ${formatClockTime(start.opensAtMinutes)}–${formatClockTime(start.closesAtMinutes)}`;
  });

  const zone = shortTimeZoneLabel(timeZoneId);
  return zone ? `${parts.join(", ")} (${zone})` : parts.join(", ");
}

/** "ET" from "America/New_York" — falls back to the raw id if the browser cannot resolve it. */
function shortTimeZoneLabel(timeZoneId: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZoneId,
      timeZoneName: "short",
    }).formatToParts(new Date());

    return parts.find((p) => p.type === "timeZoneName")?.value ?? null;
  } catch {
    return null;
  }
}

/** Somebody a ticket can be given to. A name and an id — never a directory row. */
export interface AssignableUser {
  id: string;
  displayName: string;

  /**
   * ⚠️ Optional because two endpoints produce this shape and only one carries the detail.
   *
   * `/api/tickets/{n}/assignable` is the picker — one ticket, people who may actually take
   * it — and sends job title and avatar state so the person assigning a clinical request can
   * tell two D. Nguyens apart. `/api/reference/assignees` is the queue's FILTER roster, a
   * much wider list, and deliberately still sends a name and an id.
   *
   * ⚠️ NO `email`. It was here, and the response no longer carries one: a colleague's mailbox
   * is not something a request needs to state in order to be given to them. Absent from the
   * type as well as from the wire, so nothing can start rendering one back.
   */
  jobTitle?: string | null;
  hasAvatar?: boolean;

  /**
   * How many open requests they are already carrying, as far as THIS READER can see
   * (D-123). Optional for the reason above: the queue's filter roster does not carry it.
   *
   * ⚠️ A count and nothing else. It is here so somebody choosing between two colleagues
   * can see who has more on, which is a question about the queue. It is never a rate, a
   * score or a rank, the pickers stay in NAME order rather than becoming a league table,
   * and the server refuses to publish anything shaped like a verdict (D-55, D-24).
   *
   * ⚠️ Scoped, so two leads can legitimately read different numbers beside the same name.
   * An unscoped count would answer "how much work does this colleague have beyond your
   * reach", which is a fact about them rather than about the work in front of you (D-64).
   */
  openTicketCount?: number;
}

/**
 * How the open-work figure reads beside a name in a picker (D-123).
 *
 * ⚠️ ONE DEFINITION, because three pickers draw it — the ticket's own, the bulk bar's and
 * the intake form's — and a colleague who reads as busy on one screen and idle on another is
 * worse than no figure at all.
 *
 * ⚠️ "no open requests" rather than "0 open". Zero is the useful answer here — it names the
 * person to give the work to — and a bare 0 beside a name reads as a value that failed to
 * load. Absent (the queue's filter roster does not carry the count) renders as nothing.
 */
export function workloadSuffix(person: AssignableUser): string {
  if (typeof person.openTicketCount !== "number") return "";

  return person.openTicketCount === 0 ? " (no open requests)" : ` (${person.openTicketCount} open)`;
}

/**
 * The people a request may be given to, and - when there are none - WHY (D-123).
 *
 * ⚠️ An object rather than a bare array because an empty array had ONE rendering and TWO
 * causes, and the screen asserted the rarer of them: "nobody on this team is cleared for
 * this request" was shown just as readily when the team had no members at all. The two need
 * opposite fixes, so naming the wrong one sent an administrator to the wrong page.
 */
export interface AssignableCandidates {
  candidates: AssignableUser[];

  /** Null whenever there ARE candidates, so a picker and an excuse can never both render. */
  unavailableReason: string | null;
}

/**
 * Everyone who can be given work, for the queue's assignee filter.
 *
 * ⚠️ Distinct from {@link getAssignable}, which is scoped to ONE ticket and is the only
 * thing an assignment may be made from. This is the roster, and it is gated on
 * `ticket.assign` server-side — call it only when the session holds that permission, or it
 * 403s and the filter renders empty for no visible reason.
 */
export function getAssigneeRoster(): Promise<AssignableUser[]> {
  return api<{ id: string; name: string }[]>("/api/reference/assignees").then((rows) =>
    rows.map((r) => ({ id: r.id, displayName: r.name })),
  );
}

export function getAssignable(number: string): Promise<AssignableCandidates> {
  return api<AssignableCandidates>(`/api/tickets/${encodeURIComponent(number)}/assignable`);
}

/**
 * Corrects which category a request is filed under (D-123).
 *
 * ⚠️ WITHIN ITS OWN TEAM. A category on another team is refused by the server with a
 * message naming routing, because moving a request between desks tells the people on it and
 * the team receiving it, and this does neither (D-96). There is deliberately no team
 * parameter here to tempt anybody.
 */
export function changeCategory(number: string, categoryId: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/category`, {
    method: "POST",
    body: { categoryId },
  });
}

export function assignTicket(number: string, assigneeUserId: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/api/tickets/${encodeURIComponent(number)}/assign`, {
    method: "POST",
    body: { assigneeUserId },
  });
}

/**
 * People who can take EVERY ticket in a selection.
 *
 * The intersection, computed server-side. Offering somebody who can take four of six
 * produces a half-failed bulk action the picker should not have invited.
 *
 * POST because the selection travels in the body — forty ticket numbers in a query string
 * is a URL nobody can log or debug, and it would sit in browser history.
 */
/**
 * Re-files a selection under one category (D-123).
 *
 * ⚠️ Most selections will legitimately refuse part of what was chosen - a category belongs
 * to exactly one team and a queue selection spans teams - and those come back named in the
 * refusal list this bar already draws. Silently skipping them would report a triage pass that
 * did not happen.
 */
export function bulkCategory(numbers: string[], categoryId: string): Promise<BulkResult> {
  return api<BulkResult>("/api/tickets/bulk/category", {
    method: "POST",
    body: { numbers, categoryId },
  });
}

export function bulkAssignable(numbers: string[]): Promise<AssignableUser[]> {
  return api<AssignableUser[]>("/api/tickets/bulk/assignable", {
    method: "POST",
    body: { numbers },
  });
}
